import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ScaleIcon,
  MapIcon,
  ArrowsRightLeftIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  AdjustmentsHorizontalIcon,
  ArrowUturnLeftIcon,
  CircleStackIcon,
  DocumentDuplicateIcon,
  PlusIcon,
  SparklesIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  BeakerIcon,
  QuestionMarkCircleIcon,
  PresentationChartLineIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// "estimation" est devenu l'onglet "Bilan d'opération" (fusion estimation + bilan).
// Le BilanTab autonome a disparu — le waterfall complet vit dans un accordion à l'intérieur de l'estimation.
type TabKey = 'estimation' | 'chargeFonciere' | 'scenarios' | 'calibrage';
type ModuleId = TabKey;

// Catégorisation visuelle des modules dans le panneau
type ModuleCategory = 'core' | 'analyse' | 'admin';

interface ModuleDef {
  id: ModuleId;
  label: string;        // libellé long (onglet + panneau)
  description: string;  // descriptif court dans le panneau
  required: boolean;    // si true → coché et grisé, ne peut pas être désactivé
  category: ModuleCategory;
  // L'icône est attachée dynamiquement (héroicons importés ailleurs) — on l'attribue à l'usage
}

// Liste centrale des modules — ajouter ici suffit pour qu'ils apparaissent dans le panneau "+ Modules"
// Note : le bilan d'opération n'est PAS un module séparé — il est fusionné dans "Bilan d'opération" (ex Estimation travaux)
const MODULES_LIST: ModuleDef[] = [
  { id: 'estimation',     label: "Bilan d'opération",    description: "Coût travaux par lot + waterfall économique (charge foncière, marge, frais) — vue cœur de l'étude.", required: true,  category: 'core' },
  { id: 'chargeFonciere', label: 'Charge foncière max', description: 'Calcul rétro avec sliders, stress test et enveloppe provision. Idéal en pré-acquisition.',          required: false, category: 'analyse' },
  { id: 'scenarios',      label: 'Scénarios',            description: 'Comparer 2 variantes du programme côte à côte (gamme, PV, surface, RE2025).',                       required: false, category: 'analyse' },
  { id: 'calibrage',      label: 'Calibrage',            description: 'Paramétrage métier expert des coefficients (RE, gamme, dévolution). Vue admin.',                   required: true,  category: 'admin' },
];

// Mapping module → icône — utilise ScaleIcon pour le module fusionné (sens "Bilan" prédomine)
const MODULE_ICONS: Record<ModuleId, React.ComponentType<{ className?: string }>> = {
  estimation:     ScaleIcon,
  chargeFonciere: MapIcon,
  scenarios:      ArrowsRightLeftIcon,
  calibrage:      Cog6ToothIcon,
};

const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  core:    'Modules cœur',
  analyse: 'Analyse & comparaison',
  admin:   'Paramétrage expert',
};

// État initial : modules par défaut sur une nouvelle opération
// Scénarios n'est PAS actif par défaut — l'utilisateur l'active depuis "+ Modules" quand il en a besoin
const DEFAULT_MODULES_ACTIFS: ModuleId[] = ['estimation', 'chargeFonciere', 'calibrage'];
type Gamme = 'Économique' | 'Intermédiaire' | 'Haut de gamme' | 'Très haut de gamme';
type NiveauRE2025 = 'N1' | 'N2' | 'N3';
type Typologie = 'Logement collectif' | 'Bureaux' | 'Logement individuel' | 'Mixte' | 'Hôtel';

type RegimeTVA = '5.5' | '10' | '20';
type FraisAcqPct = '5' | '7' | '8';
type Devolution = 'EG' | 'Macro-lot' | 'CES' | 'CG';
type PhaseProjet = 'Faisabilité' | 'Esquisse' | 'APS' | 'APD';

// Types de surface couramment utilisés en bâtiment
// SDP = surface de plancher (réglementaire PC) · SHOT = hors œuvre totale (incluant murs)
// SHAB = habitable (logement) · SU = utile (tertiaire) · SDO = dans œuvre (intérieur)
// SUBL/SUN = utile brute locative / nette (immobilier locatif)
type TypeSurface = 'SDP' | 'SHOT' | 'SHAB' | 'SU' | 'SDO' | 'SUBL' | 'SUN';

const TYPES_SURFACE_LABELS: Record<TypeSurface, string> = {
  'SDP':  'Surface de plancher (réglementaire PC)',
  'SHOT': "Surface hors œuvre totale (avec murs)",
  'SHAB': 'Surface habitable (logement)',
  'SU':   'Surface utile (tertiaire)',
  'SDO':  'Surface dans œuvre (intérieur)',
  'SUBL': 'Surface utile brute locative',
  'SUN':  'Surface utile nette',
};

interface Programme {
  nom: string;
  typologie: Typologie;
  sdp: number;                       // valeur numérique de la surface primaire (peu importe le type)
  typeSurface: TypeSurface;          // type de surface utilisé pour le calcul (SDP par défaut)
  surfacesComplementaires: Array<{ type: TypeSurface; value: number }>; // surfaces additionnelles (info, pas dans calcul v1)
  nbLogements: number;
  ville: string;
  gamme: Gamme;
  pvCible: number;                   // €/m² de la surface primaire HT
  niveauRE2025: NiveauRE2025;
  regimeTVA: RegimeTVA;
  fraisAcquisitionPct: FraisAcqPct;
  devolution: Devolution;
  phaseProjet: PhaseProjet;
}

const TVA_TAUX: Record<RegimeTVA, number> = {
  '5.5': 0.055,
  '10':  0.10,
  '20':  0.20,
};
const FRAIS_ACQ_TAUX: Record<FraisAcqPct, number> = {
  '5': 0.05,
  '7': 0.07,
  '8': 0.08,
};
// Dévolution → multiplicateur sur le coût travaux (composé avec les autres coefs)
const COEF_DEVOLUTION: Record<Devolution, number> = {
  'EG':        1.00, // Entreprise Générale — référence
  'Macro-lot': 0.95, // -5 % (mutualisation lots)
  'CES':       0.92, // Corps d'État Séparés : -8 % (pas de marge EG)
  'CG':        1.05, // Contractant Général : +5 % (clé en main, garantie globale)
};
// Phase projet → taux d'aléas appliqué (override le défaut 3 %)
const ALEAS_PHASE: Record<PhaseProjet, number> = {
  'Faisabilité': 0.08, // 8 % — beaucoup d'incertitudes en amont
  'Esquisse':    0.05, // 5 %
  'APS':         0.03, // 3 % — valeur par défaut
  'APD':         0.01, // 1 % — projet quasi figé
};

interface AbaqueRatios {
  // Honoraires (% du coût travaux)
  honoraires: number;
  // Frais annexes (assurances DO/TRC, taxes, raccordements) — % du CA HT
  fraisAnnexes: number;
  // Frais financiers (portage foncier + crédit construction) — % du CA HT
  fraisFinanciers: number;
  // Frais commerciaux (commercialisation, marketing) — % du CA HT
  fraisCommerciaux: number;
  // Aléas (% du coût travaux)
  aleas: number;
}

// Famille de lots (regroupement pour la vue "Famille")
type FamilleLot = 'Structure' | 'Enveloppe' | 'Second œuvre' | 'Lots techniques' | 'Aménagements';

// Configuration d'un lot avec source de données (nb projets comparables)
interface LotConfig {
  categorie: string;
  famille: FamilleLot;
  pct: number;
  nbComparables: number;
}

// Override utilisateur sur un lot — 3 modes : auto (marché), ratio €/m² fixé, montant fixé
type LotOverrideMode = 'auto' | 'ratio' | 'amount';
interface LotOverride {
  excluded: boolean;
  mode: LotOverrideMode;
  ratio?: number;  // €/m² SDP — utilisé si mode === 'ratio'
  amount?: number; // € — utilisé si mode === 'amount'
}

const DEFAULT_OVERRIDE: LotOverride = { excluded: false, mode: 'auto' };

// Niveau de fiabilité selon le nombre de projets comparables
type Fiabilite = 'haute' | 'normale' | 'limitee';
function fiabiliteFromN(n: number): Fiabilite {
  if (n >= 5) return 'haute';
  if (n >= 3) return 'normale';
  return 'limitee';
}

// Paramètres optionnels du programme — ajoutables/retirables/activables (pattern Chiffrage ParamChip)
// Tous les filtres qui modifient le calcul du coût travaux passent ici : présents ET actifs → coef appliqué
type OptionalParamKey = 'typologie' | 'gamme' | 'niveauRE2025' | 'regimeTVA' | 'fraisAcquisitionPct' | 'devolution' | 'phaseProjet';

interface OptionalParamDef {
  key: OptionalParamKey;
  label: string;       // affiché dans le chip (court)
  fullLabel: string;   // affiché dans la liste "Ajouter"
  options: Array<{ value: string; label: string }>;
}

// ---------------------------------------------------------------------------
// Constantes — ratios marché par défaut (proto, à brancher sur Ratio/Biblio)
// ---------------------------------------------------------------------------

const DEFAULT_PROGRAMME: Programme = {
  nom: 'Bordeaux Caudéran — 50 logements',
  typologie: 'Logement collectif',
  sdp: 3500,
  typeSurface: 'SDP',
  surfacesComplementaires: [],
  nbLogements: 50,
  ville: 'Bordeaux',
  gamme: 'Intermédiaire',
  pvCible: 4500,
  niveauRE2025: 'N1',
  regimeTVA: '20',
  fraisAcquisitionPct: '7',
  devolution: 'EG',
  phaseProjet: 'APS',
};

const DEFAULT_ABAQUE: AbaqueRatios = {
  honoraires: 0.12,
  fraisAnnexes: 0.04,
  fraisFinanciers: 0.03,
  fraisCommerciaux: 0.05,
  aleas: 0.03,
};

// Ratio €/m² SDP "tout-venant" — fourchette globale sans filtre actif (médiane marché toutes catégories)
const BASE_RATIO_FOURCHETTE = { min: 1600, median: 1850, max: 2200 };

// Coefficients multiplicateurs — chacun s'applique UNIQUEMENT si le filtre est actif dans le bandeau
// Permet la sémantique "pas de filtre = pas d'effet dans le calcul"
const COEF_TYPOLOGIE: Record<Typologie, number> = {
  'Logement collectif':  1.00,
  'Bureaux':             0.95,
  'Logement individuel': 0.85,
  'Mixte':               1.05,
  'Hôtel':               1.20,
};

const COEF_GAMME: Record<Gamme, number> = {
  'Économique':         0.85,
  'Intermédiaire':      1.00,
  'Haut de gamme':      1.25,
  'Très haut de gamme': 1.55,
};

const SURPRIME_RE2025: Record<NiveauRE2025, number> = {
  N1: 1.00,
  N2: 1.04,
  N3: 1.09,
};

// Mapping ville → coefficient géographique (best-effort par includes pour matcher "Bordeaux Caudéran" etc.)
const COEF_VILLE_MAP: Record<string, number> = {
  'Paris':      1.25,
  'Lyon':       1.05,
  'Bordeaux':   1.00,
  'Marseille':  0.95,
  'Toulouse':   0.95,
  'Lille':      0.95,
  'Nantes':     0.95,
  'Nice':       1.10,
  'Strasbourg': 1.00,
  'Rennes':     0.95,
};
function getCoefVille(ville: string): number {
  const v = ville.toLowerCase();
  for (const [city, coef] of Object.entries(COEF_VILLE_MAP)) {
    if (v.includes(city.toLowerCase())) return coef;
  }
  return 1.00; // ville inconnue → neutre
}

// OPTIONAL_PARAMS doit venir après COEF_GAMME (qu'il référence)
const OPTIONAL_PARAMS: OptionalParamDef[] = [
  {
    key: 'typologie',
    label: 'Typologie',
    fullLabel: 'Typologie de programme',
    options: (['Logement collectif', 'Bureaux', 'Logement individuel', 'Mixte', 'Hôtel'] as Typologie[])
      .map(t => ({ value: t, label: t })),
  },
  {
    key: 'gamme',
    label: 'Gamme',
    fullLabel: 'Gamme de prestation',
    options: (Object.keys(COEF_GAMME) as Gamme[]).map(g => ({ value: g, label: g })),
  },
  {
    key: 'niveauRE2025',
    label: 'RE2025',
    fullLabel: 'Niveau RE2025',
    options: [
      { value: 'N1', label: 'N1 (réglementaire)' },
      { value: 'N2', label: 'N2 (+4 % travaux)' },
      { value: 'N3', label: 'N3 (+9 % travaux)' },
    ],
  },
  {
    key: 'regimeTVA',
    label: 'TVA',
    fullLabel: 'Régime de TVA',
    options: [
      { value: '5.5', label: 'TVA 5,5 % (BRS/PSLA)' },
      { value: '10',  label: 'TVA 10 % (intermédiaire)' },
      { value: '20',  label: 'TVA 20 % (accession libre)' },
    ],
  },
  {
    key: 'fraisAcquisitionPct',
    label: 'Frais acquisition',
    fullLabel: "Frais d'acquisition terrain",
    options: [
      { value: '5', label: '5 % (terrain bâti)' },
      { value: '7', label: '7 % (standard terrain à bâtir)' },
      { value: '8', label: '8 % (terrain complexe)' },
    ],
  },
  {
    key: 'devolution',
    label: 'Dévolution',
    fullLabel: 'Mode de dévolution du marché',
    options: [
      { value: 'EG',        label: 'Entreprise Générale (référence)' },
      { value: 'Macro-lot', label: 'Macro-lot (−5 % travaux)' },
      { value: 'CES',       label: "Corps d'État Séparés (−8 % travaux)" },
      { value: 'CG',        label: 'Contractant Général (+5 % travaux)' },
    ],
  },
  {
    key: 'phaseProjet',
    label: 'Phase',
    fullLabel: "Phase d'étude du projet",
    options: [
      { value: 'Faisabilité', label: 'Faisabilité (aléas 8 %)' },
      { value: 'Esquisse',    label: 'Esquisse (aléas 5 %)' },
      { value: 'APS',         label: 'APS (aléas 3 %)' },
      { value: 'APD',         label: 'APD (aléas 1 %)' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Templates — presets de modules + paramètres optionnels + programme par défaut
// L'utilisateur les choisit au démarrage d'une nouvelle étude
// ---------------------------------------------------------------------------

type TemplateId = 'opportunity' | 'feasibility' | 'committee' | 'blank';

interface TemplateDef {
  id: TemplateId;
  label: string;
  tagline: string;          // 1-line subtitle visible sur la carte
  description: string;      // descriptif complet
  targetPersona: string;    // "Pour qui ?"
  accent: 'violet' | 'blue' | 'green' | 'gray';
  modules: ModuleId[];
  optionalParams: OptionalParamKey[];
  startingTab: ModuleId;
  programmePreset: Partial<Programme>;
}

const TEMPLATES_LIST: TemplateDef[] = [
  {
    id: 'opportunity',
    label: 'Opportunité foncier',
    tagline: 'Pré-acquisition · go/no-go rapide',
    description: "Vous évaluez une opportunité avant signature. Calcul rétro de la charge foncière max, marge cible, stress test au prix demandé.",
    targetPersona: 'MOA décisionnaire',
    accent: 'violet',
    modules: ['estimation', 'chargeFonciere', 'calibrage'],
    optionalParams: ['typologie', 'gamme', 'niveauRE2025', 'fraisAcquisitionPct'],
    startingTab: 'chargeFonciere',
    programmePreset: {
      nom: 'Nouvelle opportunité',
      phaseProjet: 'Faisabilité',
    },
  },
  {
    id: 'feasibility',
    label: 'Faisabilité esquisse',
    tagline: 'Esquisse acquise · estimation affinée',
    description: "Vous avez une esquisse architecte. Affinez le coût travaux par lot, personnalisez les ratios, comparez 2 variantes constructives.",
    targetPersona: 'Estimateur / AMO',
    accent: 'blue',
    modules: ['estimation', 'chargeFonciere', 'scenarios', 'calibrage'],
    optionalParams: ['typologie', 'gamme', 'devolution', 'niveauRE2025', 'phaseProjet'],
    startingTab: 'estimation',
    programmePreset: {
      nom: 'Faisabilité — esquisse',
      phaseProjet: 'Esquisse',
    },
  },
  {
    id: 'committee',
    label: "Comité d'investissement",
    tagline: 'Préparation passage en comité · 2 scénarios + deck',
    description: "Vous préparez votre passage en comité d'engagement. 2 scénarios prêts, TVA et frais d'acquisition activés, bilan complet pour le board.",
    targetPersona: 'MOA + DAF',
    accent: 'green',
    modules: ['estimation', 'chargeFonciere', 'scenarios', 'calibrage'],
    optionalParams: ['typologie', 'gamme', 'niveauRE2025', 'regimeTVA', 'fraisAcquisitionPct', 'devolution'],
    startingTab: 'scenarios',
    programmePreset: {
      nom: 'Comité — XXX',
      phaseProjet: 'APS',
    },
  },
  {
    id: 'blank',
    label: 'Étude vierge',
    tagline: 'Démarrage minimal · ajout au fil de l\'eau',
    description: 'Modules cœur seulement (Bilan d\'opération). Activez les autres modules quand vous en avez besoin via "+ Modules".',
    targetPersona: 'Tous',
    accent: 'gray',
    modules: ['estimation', 'calibrage'],
    optionalParams: ['typologie', 'gamme'],
    startingTab: 'estimation',
    programmePreset: {
      nom: 'Nouvelle étude',
    },
  },
];

const TEMPLATE_ICONS: Record<TemplateId, React.ComponentType<{ className?: string }>> = {
  opportunity: RocketLaunchIcon,
  feasibility: BeakerIcon,
  committee:   PresentationChartLineIcon,
  blank:       DocumentTextIcon,
};

// Répartition indicative par catégorie de lot (proto, avec famille + nb comparables)
const REPARTITION_LOTS: LotConfig[] = [
  { categorie: 'Terrassement / VRD',         famille: 'Structure',       pct: 0.05, nbComparables: 9  },
  { categorie: 'Gros œuvre',                 famille: 'Structure',       pct: 0.28, nbComparables: 14 },
  { categorie: 'Charpente / couverture',     famille: 'Enveloppe',       pct: 0.07, nbComparables: 11 },
  { categorie: 'Façades / menuiseries ext.', famille: 'Enveloppe',       pct: 0.13, nbComparables: 12 },
  { categorie: 'Cloisons / doublages',       famille: 'Second œuvre',    pct: 0.07, nbComparables: 10 },
  { categorie: 'Revêtements / finitions',    famille: 'Second œuvre',    pct: 0.10, nbComparables: 8  },
  { categorie: 'Plomberie / sanitaires',     famille: 'Lots techniques', pct: 0.07, nbComparables: 11 },
  { categorie: 'Électricité / CFO-CFA',      famille: 'Lots techniques', pct: 0.07, nbComparables: 10 },
  { categorie: 'CVC',                        famille: 'Lots techniques', pct: 0.10, nbComparables: 7  },
  { categorie: 'Ascenseurs',                 famille: 'Lots techniques', pct: 0.02, nbComparables: 4  },
  { categorie: 'Aménagements extérieurs',    famille: 'Aménagements',    pct: 0.04, nbComparables: 6  },
];

// Nombre de projets comparables au niveau global (croisement des filtres)
const GLOBAL_NB_COMPARABLES = 14;

// ---------------------------------------------------------------------------
// Variantes matériaux par lot — utilisées en Mode Précis dans l'onglet Estimation
// Permettent de choisir un matériau spécifique au lieu d'utiliser le ratio médian.
// Conventions : ratio en €/m² SDP (déjà mis à l'échelle), comme les overrides existants.
// ---------------------------------------------------------------------------

interface LotVariante {
  key: string;
  label: string;
  ratio: number;        // €/m² SDP — override appliqué via le mécanisme existant
  description?: string; // texte court dans le dropdown
}

const LOT_VARIANTES: Record<string, LotVariante[]> = {
  // ============================================================
  // STRUCTURE
  // ============================================================
  'Terrassement / VRD': [
    { key: 'plateforme',  label: 'Plate-forme simple',      ratio: 75,  description: 'Sol facile, terrain accessible, faible déblai' },
    { key: 'standard',    label: 'Terrassement standard',    ratio: 92,  description: 'Référence marché — sol classique, terrain urbain' },
    { key: 'complexe',    label: 'Sol complexe + géotech',   ratio: 130, description: 'Argile, nappe phréatique, géotechnique poussée' },
    { key: 'depollution', label: 'Démolition + dépollution', ratio: 165, description: 'Terrain à recycler — friches industrielles, ICPE' },
  ],
  'Gros œuvre': [
    { key: 'standard-r4',  label: 'Béton armé R+4 standard',     ratio: 480, description: 'Référence éco — petit collectif sans complexité' },
    { key: 'standard-r6',  label: 'Béton armé R+6 référence',     ratio: 518, description: 'Standard marché — bâtiment R+5 à R+7' },
    { key: 'haut-r8',      label: 'Bâtiment haut R+8 et plus',    ratio: 620, description: 'Descente de charge renforcée, parasismique zone 3+' },
    { key: 'bas-carbone',  label: 'Béton BC + structure mixte',   ratio: 715, description: 'RE2025 N2+ — béton bas carbone, façade MOB' },
  ],

  // ============================================================
  // ENVELOPPE
  // ============================================================
  'Charpente / couverture': [
    { key: 'bac-acier', label: 'Bac acier laqué',    ratio: 100, description: 'Économique — solution sèche, montage rapide' },
    { key: 'tuiles',    label: 'Tuiles standard',    ratio: 120, description: 'Référence marché — couverture la plus courante' },
    { key: 'ardoise',   label: 'Ardoise naturelle',  ratio: 145, description: 'Patrimoine — restauration, secteur sauvegardé' },
    { key: 'zinc',      label: 'Zinc joint debout',  ratio: 195, description: 'Premium architectural — toiture-terrasse type Mansart' },
  ],
  'Façades / menuiseries ext.': [
    { key: 'enduit',         label: 'Enduit + MEXT PVC',           ratio: 195, description: 'Standard économique — logement intermédiaire' },
    { key: 'plaquette-tc',   label: 'Plaquette terre cuite + ALU', ratio: 235, description: 'Référence courante — secteur libre' },
    { key: 'bardage-bois',   label: 'Bardage bois + BOIS-ALU',     ratio: 280, description: 'Premium contemporain — éco-quartier, RE2025+' },
    { key: 'pierre-agrafee', label: 'Pierre agrafée + ALU',        ratio: 340, description: 'Haut de gamme prestige — centre-ville, livraison VIP' },
  ],

  // ============================================================
  // SECOND ŒUVRE
  // ============================================================
  'Cloisons / doublages': [
    { key: 'ba13-eco',    label: 'BA13 standard',                ratio: 110, description: 'Notice A — logement social, intermédiaire' },
    { key: 'standard',    label: 'BA13 + acoustique correcte',    ratio: 130, description: 'Référence marché — secteur libre standard' },
    { key: 'acoustique',  label: 'Acoustique renforcée',          ratio: 165, description: 'Haut de gamme — isolation phonique entre logements' },
    { key: 'biosource',   label: 'Cloisons biosourcées',          ratio: 195, description: 'RE2025+ — fibres bois, paille, label BBCA' },
  ],
  'Revêtements / finitions': [
    { key: 'eco',      label: 'Sol PVC + peinture standard',     ratio: 165, description: 'Notice A — économique, logement social' },
    { key: 'intermed', label: 'Stratifié + peinture qualité',     ratio: 195, description: 'Notice intermédiaire — référence marché' },
    { key: 'premium',  label: 'Parquet massif + carrelage haut',  ratio: 245, description: 'Notice B — accession haut de gamme' },
  ],

  // ============================================================
  // LOTS TECHNIQUES
  // ============================================================
  'Plomberie / sanitaires': [
    { key: 'collectif',  label: 'Sanitaires collectif standard',     ratio: 110, description: 'Notice A — robinetterie économique' },
    { key: 'standard',   label: 'Sanitaires référence',              ratio: 130, description: 'Notice intermédiaire — qualité marché' },
    { key: 'hdg',        label: 'Mitigeurs design + douche italienne', ratio: 175, description: 'Notice B — haut de gamme, parois verre' },
    { key: 'premium',    label: 'Robinetterie premium + balnéo',      ratio: 220, description: 'Très haut de gamme — baignoire balnéo, sèche-serviettes' },
  ],
  'Électricité / CFO-CFA': [
    { key: 'promotelec', label: 'Promotelec confort',         ratio: 110, description: 'Notice A — minimum réglementaire' },
    { key: 'standard',   label: 'Référence marché',            ratio: 130, description: 'Notice intermédiaire — prises additionnelles' },
    { key: 'connecte',   label: 'Domotique standard connectée', ratio: 170, description: 'RE2025 + Smart Home basique — volets, chauffage pilotables' },
    { key: 'smart',      label: 'KNX / Domotique premium',      ratio: 220, description: 'Très haut de gamme — bus KNX, scénarios, audio multiroom' },
  ],
  'CVC': [
    { key: 'reseau-urbain',  label: 'Raccordement réseau urbain', ratio: 130, description: 'Économique — si réseau dispo, conformité RE2020' },
    { key: 'chaudiere-coll', label: 'Chaudière gaz collective',  ratio: 175, description: 'Référence — logement collectif standard' },
    { key: 'pac-coll',       label: 'PAC géothermie collective', ratio: 245, description: 'RE2025 N2+ — économies à long terme' },
  ],
  'Ascenseurs': [
    { key: 'standard', label: 'Ascenseur 8 personnes standard',   ratio: 30, description: '1 cage par bâtiment — gabarit normalisé' },
    { key: 'reference', label: 'Ascenseur référence + cabines 2', ratio: 37, description: 'Standard marché — 2 cages ou 1 grande' },
    { key: 'premium',   label: 'Ascenseurs prestige + monte-charge', ratio: 55, description: 'Très haut de gamme — finitions cabine luxe + monte-charge service' },
  ],

  // ============================================================
  // AMÉNAGEMENTS
  // ============================================================
  'Aménagements extérieurs': [
    { key: 'basique',  label: 'VRD basique + plantations',           ratio: 55,  description: 'Terrain plat, plantations simples, clôture standard' },
    { key: 'standard', label: 'Espaces verts + clôtures référence',  ratio: 74,  description: 'Standard marché — paysagiste minimal, mobilier urbain' },
    { key: 'paysager', label: 'Création paysagère premium',          ratio: 110, description: 'Sujets adultes, allées en pierre, éclairage soigné' },
    { key: 'bbca',     label: 'Pleine terre + bassin biodiversité',  ratio: 145, description: 'Label BBCA / biodiversité — gestion eaux pluviales, mare' },
  ],
};

// Ordre d'affichage des familles
const FAMILLES_ORDER: FamilleLot[] = ['Structure', 'Enveloppe', 'Second œuvre', 'Lots techniques', 'Aménagements'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatEuro = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} €`;
const formatEuroM = (n: number) => `${(n / 1_000_000).toFixed(2)} M€`;
const formatPct = (n: number) => `${(n * 100).toFixed(1)} %`;
const formatInt = (n: number) => `${Math.round(n).toLocaleString('fr-FR')}`;

interface BilanCompute {
  caHt: number;
  caTtc: number;          // = caHt × (1 + tauxTVA) si TVA active, sinon = caHt
  tauxTVA: number;        // 0 si non actif
  ratioTravaux: number;
  travaux: number;
  honoraires: number;
  fraisAnnexes: number;
  fraisFinanciers: number;
  fraisCommerciaux: number;
  aleas: number;
  totalHorsTerrain: number;
}

// % éditables du waterfall — remontés au parent EtudeOpportunite pour que le
// KPI strip et le waterfall détaillé partagent la même source de vérité.
interface LocalPcts {
  honoraires: number;
  fraisAnnexes: number;
  fraisFinanciers: number;
  fraisCommerciaux: number;
  margeCible: number;
}

function computeBilan(
  p: Programme,
  a: AbaqueRatios,
  activeOptional: Set<OptionalParamKey>,
  travauxOverride?: number,
): BilanCompute {
  const caHt = p.pvCible * p.sdp;
  const tauxTVA = activeOptional.has('regimeTVA') ? TVA_TAUX[p.regimeTVA] : 0;
  const caTtc = caHt * (1 + tauxTVA);
  const ratioTravaux = BASE_RATIO_FOURCHETTE.median * getCoefCumule(p, activeOptional);
  const travauxBase = ratioTravaux * p.sdp;
  const travaux = travauxOverride !== undefined ? travauxOverride : travauxBase;
  const honoraires = a.honoraires * travaux;
  const fraisAnnexes = a.fraisAnnexes * caHt;
  const fraisFinanciers = a.fraisFinanciers * caHt;
  const fraisCommerciaux = a.fraisCommerciaux * caHt;
  const aleasPct = getEffectiveAleasPct(p, a, activeOptional);
  const aleas = aleasPct * travaux;
  const totalHorsTerrain =
    travaux + honoraires + fraisAnnexes + fraisFinanciers + fraisCommerciaux + aleas;
  return {
    caHt,
    caTtc,
    tauxTVA,
    ratioTravaux,
    travaux,
    honoraires,
    fraisAnnexes,
    fraisFinanciers,
    fraisCommerciaux,
    aleas,
    totalHorsTerrain,
  };
}

// Décompose une charge foncière totale (= ce qu'on peut payer au total) en prix d'achat
// au vendeur + frais d'acquisition (notaire, droits d'enregistrement, etc.)
function decomposerCharge(
  chargeFonciereTotale: number,
  programme: Programme,
  activeOptional: Set<OptionalParamKey>,
): { prixAchat: number; frais: number; tauxFrais: number } {
  const tauxFrais = activeOptional.has('fraisAcquisitionPct') ? FRAIS_ACQ_TAUX[programme.fraisAcquisitionPct] : 0;
  const prixAchat = chargeFonciereTotale / (1 + tauxFrais);
  const frais = chargeFonciereTotale - prixAchat;
  return { prixAchat, frais, tauxFrais };
}

// Calcule le coût effectif d'un lot, en tenant compte d'un éventuel override utilisateur
function getEffectiveLotCout(
  lot: LotConfig,
  override: LotOverride | undefined,
  baseTravaux: number,
  programme: Programme,
): number {
  const ov = override ?? DEFAULT_OVERRIDE;
  if (ov.excluded) return 0;
  if (ov.mode === 'ratio' && ov.ratio !== undefined && ov.ratio > 0) {
    return ov.ratio * programme.sdp;
  }
  if (ov.mode === 'amount' && ov.amount !== undefined && ov.amount > 0) {
    return ov.amount;
  }
  return baseTravaux * lot.pct;
}

// Somme des coûts effectifs de tous les lots (= travaux effectif global)
function getTotalTravauxEffectif(
  overrides: Map<string, LotOverride>,
  baseTravaux: number,
  programme: Programme,
): number {
  return REPARTITION_LOTS.reduce(
    (sum, lot) => sum + getEffectiveLotCout(lot, overrides.get(lot.categorie), baseTravaux, programme),
    0,
  );
}

// Coefficient cumulé travaux — composé de tous les filtres actifs
// Sémantique : un filtre absent (non ajouté) OU désactivé n'a aucun effet (coef 1.00)
// Robuste aux valeurs undefined (hot reload de l'app en cours de dev)
function getCoefCumule(programme: Programme, activeOptional: Set<OptionalParamKey>): number {
  let coef = 1;
  if (activeOptional.has('typologie')    && COEF_TYPOLOGIE[programme.typologie]      !== undefined) coef *= COEF_TYPOLOGIE[programme.typologie];
  if (activeOptional.has('gamme')        && COEF_GAMME[programme.gamme]              !== undefined) coef *= COEF_GAMME[programme.gamme];
  if (activeOptional.has('devolution')   && COEF_DEVOLUTION[programme.devolution]    !== undefined) coef *= COEF_DEVOLUTION[programme.devolution];
  if (activeOptional.has('niveauRE2025') && SURPRIME_RE2025[programme.niveauRE2025]  !== undefined) coef *= SURPRIME_RE2025[programme.niveauRE2025];
  coef *= getCoefVille(programme.ville || ''); // toujours appliqué (ville inconnue = 1.00)
  return coef;
}

// Détail des coefs actifs (pour affichage / tooltip)
function getCoefBreakdown(programme: Programme, activeOptional: Set<OptionalParamKey>): Array<{ label: string; coef: number }> {
  const parts: Array<{ label: string; coef: number }> = [];
  if (activeOptional.has('typologie')    && COEF_TYPOLOGIE[programme.typologie]      !== undefined) parts.push({ label: programme.typologie,                  coef: COEF_TYPOLOGIE[programme.typologie] });
  if (activeOptional.has('gamme')        && COEF_GAMME[programme.gamme]              !== undefined) parts.push({ label: programme.gamme,                      coef: COEF_GAMME[programme.gamme] });
  if (activeOptional.has('devolution')   && COEF_DEVOLUTION[programme.devolution]    !== undefined) parts.push({ label: `Dévolution ${programme.devolution}`, coef: COEF_DEVOLUTION[programme.devolution] });
  if (activeOptional.has('niveauRE2025') && SURPRIME_RE2025[programme.niveauRE2025]  !== undefined) parts.push({ label: `RE2025 ${programme.niveauRE2025}`,   coef: SURPRIME_RE2025[programme.niveauRE2025] });
  const cv = getCoefVille(programme.ville || '');
  if (cv !== 1.00) parts.push({ label: programme.ville, coef: cv });
  return parts;
}

// Taux d'aléas effectif — overridé par la phase si le paramètre est actif, sinon défaut de l'abaque
function getEffectiveAleasPct(programme: Programme, abaque: AbaqueRatios, activeOptional: Set<OptionalParamKey>): number {
  if (activeOptional.has('phaseProjet') && ALEAS_PHASE[programme.phaseProjet] !== undefined) {
    return ALEAS_PHASE[programme.phaseProjet];
  }
  return abaque.aleas;
}

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------

const EtudeOpportunite: React.FC = () => {
  const [programme, setProgramme] = useState<Programme>(DEFAULT_PROGRAMME);
  const [activeTab, setActiveTab] = useState<TabKey>('estimation');
  const [abaque] = useState<AbaqueRatios>(DEFAULT_ABAQUE);
  // Overrides utilisateur par lot — partagé entre les onglets pour cohérence (Bilan + Charge foncière reflètent les exclusions/ajustements)
  const [lotOverrides, setLotOverrides] = useState<Map<string, LotOverride>>(new Map());
  // Paramètres optionnels visibles + actifs (pattern Chiffrage)
  // Par défaut on inclut typologie + gamme + RE2025 (les plus courants) ; TVA et Frais restent à ajouter à la demande
  const [visibleOptional, setVisibleOptional] = useState<Set<OptionalParamKey>>(() => new Set<OptionalParamKey>(['typologie', 'gamme', 'niveauRE2025']));
  const [activeOptional, setActiveOptional]   = useState<Set<OptionalParamKey>>(() => new Set<OptionalParamKey>(['typologie', 'gamme', 'niveauRE2025']));

  // Modules activables — l'opération porte sa propre configuration de modules
  // Tout collaborateur ouvrant cette opération voit les mêmes modules actifs
  const [modulesActifs, setModulesActifs] = useState<Set<ModuleId>>(() => new Set<ModuleId>(DEFAULT_MODULES_ACTIFS));
  // Modale "Nouvelle étude" — affiche les templates au démarrage
  const [isNewStudyOpen, setIsNewStudyOpen] = useState(false);

  // Application d'un template — reset complet de l'opération avec les presets choisis
  const applyTemplate = (template: TemplateDef) => {
    setProgramme({ ...DEFAULT_PROGRAMME, ...template.programmePreset });
    setModulesActifs(new Set<ModuleId>(template.modules));
    setVisibleOptional(new Set<OptionalParamKey>(template.optionalParams));
    setActiveOptional(new Set<OptionalParamKey>(template.optionalParams));
    setLotOverrides(new Map());
    setActiveTab(template.startingTab);
    setIsNewStudyOpen(false);
  };
  const toggleModule = (id: ModuleId) => {
    const def = MODULES_LIST.find(m => m.id === id);
    if (def?.required) return; // protection : modules requis non désactivables
    setModulesActifs(prev => {
      const next = new Set<ModuleId>(prev);
      if (next.has(id)) {
        next.delete(id);
        // Si l'onglet actif vient d'être désactivé → fallback sur Estimation
        if (activeTab === id) setActiveTab('estimation');
      } else {
        next.add(id);
        setActiveTab(id); // basculer directement sur l'onglet fraîchement activé
      }
      return next;
    });
  };

  // Coût travaux "marché" (sans override) — chaque filtre actif applique son coef multiplicateur
  // Filtre absent/désactivé = pas d'effet sur le calcul (coef neutre 1.00)
  const baseTravaux = useMemo(
    () => BASE_RATIO_FOURCHETTE.median * getCoefCumule(programme, activeOptional) * programme.sdp,
    [programme, activeOptional],
  );

  // Coût travaux effectif (intègre exclusions + overrides)
  const travauxEffectif = useMemo(
    () => getTotalTravauxEffectif(lotOverrides, baseTravaux, programme),
    [lotOverrides, baseTravaux, programme],
  );

  // Bilan recalculé sur le travaux effectif → impacte aussi Bilan d'opération + Charge foncière
  const bilan = useMemo(
    () => computeBilan(programme, abaque, activeOptional, travauxEffectif),
    [programme, abaque, activeOptional, travauxEffectif],
  );

  // % éditables du waterfall — remontés ici pour que le KPI strip (EstimationBilanFusion)
  // et le waterfall détaillé (BilanTab) partagent la même source de vérité.
  const [localPcts, setLocalPcts] = useState<LocalPcts>({
    honoraires:       abaque.honoraires,
    fraisAnnexes:     abaque.fraisAnnexes,
    fraisFinanciers:  abaque.fraisFinanciers,
    fraisCommerciaux: abaque.fraisCommerciaux,
    margeCible:       0.08,
  });
  // Mode bidirectionnel : si l'utilisateur édite la charge foncière, on l'override
  // et la marge se recalcule ; éditer la marge ré-active le mode "marge fixe".
  const [chargeFonciereOverride, setChargeFonciereOverride] = useState<number | null>(null);

  const setPct = (key: keyof LocalPcts, v: number) => {
    setLocalPcts(prev => ({ ...prev, [key]: Math.max(0, v) }));
    if (key === 'margeCible') setChargeFonciereOverride(null);
  };

  // Valeurs effectives partagées entre KPI strip et waterfall détaillé
  const bilanEff = useMemo(() => {
    const honorairesEff       = localPcts.honoraires       * bilan.travaux;
    const fraisAnnexesEff     = localPcts.fraisAnnexes     * bilan.caHt;
    const fraisFinanciersEff  = localPcts.fraisFinanciers  * bilan.caHt;
    const fraisCommerciauxEff = localPcts.fraisCommerciaux * bilan.caHt;
    const totalHorsTerrainEff =
      bilan.travaux + honorairesEff + fraisAnnexesEff + fraisFinanciersEff + fraisCommerciauxEff + bilan.aleas;

    let margeCibleEff: number;
    let chargeFonciereImpliciteEff: number;
    let margeCiblePctEff: number;
    if (chargeFonciereOverride !== null) {
      chargeFonciereImpliciteEff = chargeFonciereOverride;
      margeCibleEff = bilan.caHt - totalHorsTerrainEff - chargeFonciereImpliciteEff;
      margeCiblePctEff = bilan.caHt > 0 ? margeCibleEff / bilan.caHt : 0;
    } else {
      margeCibleEff = localPcts.margeCible * bilan.caHt;
      chargeFonciereImpliciteEff = bilan.caHt - totalHorsTerrainEff - margeCibleEff;
      margeCiblePctEff = localPcts.margeCible;
    }

    return {
      honorairesEff,
      fraisAnnexesEff,
      fraisFinanciersEff,
      fraisCommerciauxEff,
      totalHorsTerrainEff,
      margeCibleEff,
      margeCiblePctEff,
      chargeFonciereImpliciteEff,
    };
  }, [localPcts, chargeFonciereOverride, bilan]);

  // Indicateur de présence d'overrides (pour afficher un badge "Personnalisé" au global)
  const hasOverrides = lotOverrides.size > 0;

  const updateOverride = (categorie: string, partial: Partial<LotOverride>) => {
    setLotOverrides(prev => {
      const next = new Map(prev);
      const current = next.get(categorie) ?? DEFAULT_OVERRIDE;
      next.set(categorie, { ...current, ...partial });
      return next;
    });
  };

  const resetOverride = (categorie: string) => {
    setLotOverrides(prev => {
      const next = new Map(prev);
      next.delete(categorie);
      return next;
    });
  };

  const resetAllOverrides = () => setLotOverrides(new Map());

  // Gestion des paramètres optionnels (pattern Chiffrage)
  const addOptional = (key: OptionalParamKey) => {
    setVisibleOptional(prev => { const next = new Set<OptionalParamKey>(prev); next.add(key); return next; });
    setActiveOptional(prev => { const next = new Set<OptionalParamKey>(prev); next.add(key); return next; });
  };
  const removeOptional = (key: OptionalParamKey) => {
    setVisibleOptional(prev => { const next = new Set<OptionalParamKey>(prev); next.delete(key); return next; });
    setActiveOptional(prev => { const next = new Set<OptionalParamKey>(prev); next.delete(key); return next; });
  };
  const toggleOptional = (key: OptionalParamKey) => {
    setActiveOptional(prev => {
      const next = new Set<OptionalParamKey>(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-24">
        {/* Header */}
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-violet-700 leading-tight">
              Étude d'opportunité
            </h1>
            <p className="mt-2 text-base text-gray-600">
              Évaluer la faisabilité économique d'une opération à partir d'un programme prévisionnel
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setIsNewStudyOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-violet-300 hover:text-violet-700 transition-colors"
            >
              <PlusCircleIcon className="w-4 h-4" />
              Nouvelle étude
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold shadow-sm hover:bg-violet-700 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Exporter le deck
            </button>
          </div>
        </header>

        {/* Bandeau Programme — pattern Sillant (ParamChip + AddButton) */}
        <section className="mb-6 sticky top-0 z-20 bg-gray-50 pt-2 pb-1 -mx-4 px-4">
          <ProgrammeBandeau
            programme={programme}
            onChange={setProgramme}
            visibleOptional={visibleOptional}
            activeOptional={activeOptional}
            onAddOptional={addOptional}
            onRemoveOptional={removeOptional}
            onToggleOptional={toggleOptional}
          />
        </section>

        {/* Navigation Tabs — filtrée par les modules actifs (catégories core + analyse).
            Calibrage (admin) et "+ Modules" sont sortis de la nav et fixés à droite. */}
        <section className="mb-6">
          <div className="border-b border-gray-200 flex items-end gap-3">
            <nav className="-mb-px flex items-end space-x-8 overflow-x-auto flex-1 min-w-0 scrollbar-thin">
              {MODULES_LIST.filter(m => modulesActifs.has(m.id) && m.category !== 'admin').map(m => (
                <TabButton
                  key={m.id}
                  active={activeTab === m.id}
                  onClick={() => setActiveTab(m.id)}
                  icon={MODULE_ICONS[m.id]}
                  label={m.label}
                />
              ))}
            </nav>
            {/* Boutons admin à droite — sortis de la nav scrollable. Gradient signale la bascule. */}
            <div className="relative flex-shrink-0 pb-2 flex items-center gap-2">
              <div className="absolute right-full top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none" aria-hidden="true" />
              {modulesActifs.has('calibrage') && (
                <button
                  type="button"
                  onClick={() => setActiveTab(activeTab === 'calibrage' ? 'estimation' : 'calibrage')}
                  title={activeTab === 'calibrage'
                    ? 'Retour à Estimation travaux'
                    : 'Calibrage — Paramétrage métier expert des coefficients (RE, gamme, dévolution)'}
                  aria-label="Calibrage"
                  aria-pressed={activeTab === 'calibrage'}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                    activeTab === 'calibrage'
                      ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-200'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                </button>
              )}
              <ModulesPanel modulesActifs={modulesActifs} onToggle={toggleModule} />
            </div>
          </div>
        </section>

        {/* Tab Content */}
        {/* Module fusionné "Bilan d'opération" : KPI strip + estimation travaux + waterfall accordion */}
        {activeTab === 'estimation' && (
          <EstimationBilanFusion
            programme={programme}
            bilan={bilan}
            abaque={abaque}
            baseTravaux={baseTravaux}
            hasOverrides={hasOverrides}
            activeOptional={activeOptional}
            lotOverrides={lotOverrides}
            onUpdateOverride={updateOverride}
            onResetOverride={resetOverride}
            onResetAll={resetAllOverrides}
            onActivatePhase={() => {
              if (!visibleOptional.has('phaseProjet')) addOptional('phaseProjet');
              else if (!activeOptional.has('phaseProjet')) toggleOptional('phaseProjet');
            }}
            localPcts={localPcts}
            setPct={setPct}
            chargeFonciereOverride={chargeFonciereOverride}
            setChargeFonciereOverride={setChargeFonciereOverride}
            honorairesEff={bilanEff.honorairesEff}
            fraisAnnexesEff={bilanEff.fraisAnnexesEff}
            fraisFinanciersEff={bilanEff.fraisFinanciersEff}
            fraisCommerciauxEff={bilanEff.fraisCommerciauxEff}
            totalHorsTerrainEff={bilanEff.totalHorsTerrainEff}
            margeCibleEff={bilanEff.margeCibleEff}
            margeCiblePctEff={bilanEff.margeCiblePctEff}
            chargeFonciereImpliciteEff={bilanEff.chargeFonciereImpliciteEff}
          />
        )}
        {activeTab === 'chargeFonciere' && (
          <ChargeFonciereTab
            programme={programme}
            bilan={bilan}
            hasOverrides={hasOverrides}
            activeOptional={activeOptional}
            localPcts={localPcts}
            setPct={setPct}
          />
        )}
        {activeTab === 'scenarios' && (
          <ScenariosTab
            programme={programme}
            abaque={abaque}
            localPcts={localPcts}
            activeOptional={activeOptional}
          />
        )}
        {activeTab === 'calibrage' && (
          <CalibrageTab />
        )}
      </div>

      {/* Modale Nouvelle étude — affiche les templates au démarrage */}
      {isNewStudyOpen && (
        <NewStudyModal
          onClose={() => setIsNewStudyOpen(false)}
          onChoose={applyTemplate}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ProgrammeBandeau — pattern Sillant (ParamChip + AddParamButton de Chiffrage)
// Tous les paramètres sont des chips violets minimalistes, ajoutables/retirables/activables
// ---------------------------------------------------------------------------

interface ProgrammeBandeauProps {
  programme: Programme;
  onChange: (p: Programme) => void;
  visibleOptional: Set<OptionalParamKey>;
  activeOptional: Set<OptionalParamKey>;
  onAddOptional: (key: OptionalParamKey) => void;
  onRemoveOptional: (key: OptionalParamKey) => void;
  onToggleOptional: (key: OptionalParamKey) => void;
}

const ProgrammeBandeau: React.FC<ProgrammeBandeauProps> = ({
  programme, onChange,
  visibleOptional, activeOptional,
  onAddOptional, onRemoveOptional, onToggleOptional,
}) => {
  const set = <K extends keyof Programme>(key: K, value: Programme[K]) =>
    onChange({ ...programme, [key]: value });

  const availableOptional = OPTIONAL_PARAMS.filter(d => !visibleOptional.has(d.key));

  return (
    <div className="bg-gradient-to-br from-violet-50/40 to-white border border-violet-100 rounded-xl shadow-sm p-4">
      {/* Row 1 : titre cliquable + actions */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <EditableProjectTitle value={programme.nom} onChange={(v) => set('nom', v)} />
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-violet-400 hover:text-violet-700 transition-colors"
            title="Dupliquer en variante"
          >
            <DocumentDuplicateIcon className="w-3.5 h-3.5" />
            Dupliquer
          </button>
        </div>
      </div>

      {/* Row 2 : chips de paramètres + bouton Ajouter */}
      <div className="flex items-center gap-1.5 flex-wrap min-h-[26px]">
        {/* Paramètres "core" — saisies obligatoires (SDP/Logements/PV/Ville) */}
        {/* La Ville est core et toujours présente — son coef est appliqué via getCoefVille (1.00 si ville inconnue) */}
        <ProgChip
          fullLabel="Ville"
          type="text"
          value={programme.ville}
          onValueChange={(v) => set('ville', v)}
        />
        {/* Surface primaire : type sélectionnable (SDP/SHOT/SHAB/SU/SDO/SUBL/SUN) + valeur */}
        <ProgChip
          fullLabel={`Type de surface — ${TYPES_SURFACE_LABELS[programme.typeSurface]}`}
          type="enum"
          value={programme.typeSurface}
          displayLabel={programme.typeSurface}
          options={(Object.keys(TYPES_SURFACE_LABELS) as TypeSurface[]).map(t => ({ value: t, label: `${t} — ${TYPES_SURFACE_LABELS[t]}` }))}
          onValueChange={(v) => set('typeSurface', v as TypeSurface)}
        />
        <ProgChip
          fullLabel={`Valeur de la surface (${programme.typeSurface})`}
          type="number"
          value={String(programme.sdp)}
          unit="m²"
          onValueChange={(v) => set('sdp', Number(v) || 0)}
        />

        {/* Surfaces complémentaires (info contextuelle, pas dans le calcul v1) */}
        <SecondarySurfacesEditor
          primaryType={programme.typeSurface}
          surfaces={programme.surfacesComplementaires}
          onChange={(next) => onChange({ ...programme, surfacesComplementaires: next })}
        />

        <ProgChip
          fullLabel="Nombre de logements"
          type="number"
          value={String(programme.nbLogements)}
          unit="logts"
          onValueChange={(v) => set('nbLogements', Number(v) || 0)}
        />
        <ProgChip
          fullLabel="Prix de vente cible"
          type="number"
          value={String(programme.pvCible)}
          unit="€/m²"
          onValueChange={(v) => set('pvCible', Number(v) || 0)}
        />

        {/* Paramètres optionnels — ajoutables / retirables / désactivables (Typologie, Gamme, RE2025, TVA, Frais) */}
        {/* Chacun applique son coef uniquement s'il est actif. Pattern : filtre absent = pas d'effet sur le calcul. */}
        {Array.from(visibleOptional).map(key => {
          const def = OPTIONAL_PARAMS.find(d => d.key === key);
          if (!def) return null;
          const isActive = activeOptional.has(key);
          const currentValue = String(programme[key]);
          return (
            <ProgChip
              key={key}
              fullLabel={def.fullLabel}
              type="enum"
              value={currentValue}
              displayLabel={`${def.label} ${currentValue}`}
              options={def.options}
              isActive={isActive}
              canToggle
              canRemove
              onValueChange={(v) => set(key, v as Programme[typeof key])}
              onToggle={() => onToggleOptional(key)}
              onRemove={() => onRemoveOptional(key)}
            />
          );
        })}

        {/* Bouton Ajouter */}
        {availableOptional.length > 0 && (
          <AddProgChipButton available={availableOptional} onAdd={onAddOptional} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SecondarySurfacesEditor — chips de surfaces complémentaires (SHOT, SHAB, etc.)
// La surface primaire est gérée séparément ; ici on saisit des références additionnelles
// (info contextuelle ; le câblage par lot viendra plus tard)
// ---------------------------------------------------------------------------

interface SecondarySurfacesEditorProps {
  primaryType: TypeSurface;
  surfaces: Array<{ type: TypeSurface; value: number }>;
  onChange: (next: Array<{ type: TypeSurface; value: number }>) => void;
}

const SecondarySurfacesEditor: React.FC<SecondarySurfacesEditorProps> = ({ primaryType, surfaces, onChange }) => {
  const usedTypes = new Set<TypeSurface>([primaryType, ...surfaces.map(s => s.type)]);
  const availableTypes = (Object.keys(TYPES_SURFACE_LABELS) as TypeSurface[]).filter(t => !usedTypes.has(t));

  const updateValue = (idx: number, value: number) => {
    onChange(surfaces.map((s, i) => (i === idx ? { ...s, value } : s)));
  };
  const removeAt = (idx: number) => onChange(surfaces.filter((_, i) => i !== idx));
  const addSurface = (type: TypeSurface) => onChange([...surfaces, { type, value: 0 }]);

  return (
    <>
      {surfaces.map((s, idx) => (
        <ProgChip
          key={`${s.type}-${idx}`}
          fullLabel={`${s.type} — ${TYPES_SURFACE_LABELS[s.type]} (complémentaire, info uniquement)`}
          type="number"
          value={String(s.value)}
          unit="m²"
          displayLabel={`${s.type} ${formatInt(s.value)} m²`}
          canRemove
          onValueChange={(v) => updateValue(idx, Number(v) || 0)}
          onRemove={() => removeAt(idx)}
        />
      ))}
      {availableTypes.length > 0 && (
        <AddSurfaceButton available={availableTypes} onAdd={addSurface} />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// AddSurfaceButton — pendant de AddProgChipButton, ajoute une surface complémentaire
// ---------------------------------------------------------------------------

interface AddSurfaceButtonProps {
  available: TypeSurface[];
  onAdd: (type: TypeSurface) => void;
}

const AddSurfaceButton: React.FC<AddSurfaceButtonProps> = ({ available, onAdd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-400 hover:text-violet-600 hover:bg-violet-50 border border-dashed border-gray-300 hover:border-violet-300 rounded-full transition-colors"
        title="Ajouter une surface complémentaire (info, pas dans le calcul v1)"
      >
        <PlusIcon className="w-3 h-3" />
        Surface
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[260px]">
          <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
            Ajouter une surface complémentaire
          </div>
          {available.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => { onAdd(t); setIsOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-violet-50 hover:text-violet-700"
            >
              <span className="font-semibold tabular-nums">{t}</span>
              <span className="text-gray-500"> — {TYPES_SURFACE_LABELS[t]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// EditableProjectTitle — titre cliquable pour renommer (pattern ProjectHeader)
// ---------------------------------------------------------------------------

const EditableProjectTitle: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const start = () => { setDraft(value); setEditing(true); };
  const commit = () => { onChange(draft.trim() || value); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (editing) {
    return (
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        autoFocus
        className="flex-1 min-w-0 text-xl font-semibold text-gray-900 border-none outline-none focus:ring-2 focus:ring-violet-500 rounded px-2 py-1 bg-white"
        placeholder="Nom de l'opération"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      title="Cliquer pour renommer"
      className="group flex items-center gap-2 cursor-pointer hover:bg-white/60 rounded-lg px-2 py-1 -mx-2 -my-1 transition-all text-left min-w-0"
    >
      <h2 className="text-xl font-semibold truncate text-gray-900">{value}</h2>
      <PencilSquareIcon className="w-4 h-4 text-gray-400 group-hover:text-violet-600 transition-colors opacity-60 group-hover:opacity-100 flex-shrink-0" />
    </button>
  );
};

// ---------------------------------------------------------------------------
// ProgChip — chip universel (enum / number / text), pattern ParamChip de Chiffrage
// ---------------------------------------------------------------------------

interface ProgChipProps {
  fullLabel: string;       // tooltip + label de l'éditeur
  type: 'enum' | 'number' | 'text';
  value: string;
  displayLabel?: string;   // ce qui s'affiche dans la chip (sinon = value)
  options?: Array<{ value: string; label: string }>;  // pour enum
  unit?: string;           // pour number
  isActive?: boolean;      // défaut true ; si false → grisé + barré
  canToggle?: boolean;     // défaut false (click sur valeur ouvre l'édition)
  canRemove?: boolean;     // défaut false
  onValueChange: (v: string) => void;
  onToggle?: () => void;
  onRemove?: () => void;
}

const ProgChip: React.FC<ProgChipProps> = ({
  fullLabel, type, value, displayLabel, options = [], unit,
  isActive = true, canToggle = false, canRemove = false,
  onValueChange, onToggle, onRemove,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  const containerClass = isActive
    ? 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
    : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100';

  const openEditor = () => {
    if (type === 'enum') {
      setIsDropdownOpen(o => !o);
    } else {
      setDraft(value);
      setIsEditing(true);
    }
  };

  const commitEdit = () => {
    onValueChange(draft.trim() || value);
    setIsEditing(false);
  };
  const cancelEdit = () => { setDraft(value); setIsEditing(false); };

  // Action principale du click sur la valeur : toggle si possible, sinon édition
  const onValueClick = canToggle && onToggle ? onToggle : openEditor;

  const shownLabel = displayLabel ?? (type === 'number' && unit ? `${formatInt(Number(value) || 0)} ${unit}` : value);

  return (
    <div ref={ref} className="relative inline-flex items-center group">
      {/* Tooltip custom — apparaît au hover avec le label métier (instantané, plus visible que title HTML) */}
      {!isEditing && !isDropdownOpen && (
        <div
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-900 text-white text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 delay-100 z-50 shadow-lg"
        >
          {fullLabel}
          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
        </div>
      )}
      <div className={`inline-flex items-center gap-1 border rounded-full pl-2.5 pr-1 py-0.5 text-xs transition-colors ${containerClass}`} title={fullLabel}>
        {/* Valeur (cliquable) */}
        {isEditing ? (
          <input
            type={type === 'number' ? 'number' : 'text'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
            }}
            autoFocus
            className="text-xs bg-white border border-violet-300 rounded px-1 py-px outline-none focus:ring-1 focus:ring-violet-400 w-20 tabular-nums"
          />
        ) : (
          <button
            type="button"
            onClick={onValueClick}
            title={
              canToggle
                ? `${fullLabel} — cliquer pour ${isActive ? 'désactiver' : 'activer'}`
                : `${fullLabel} — cliquer pour modifier`
            }
            className={`text-xs tabular-nums ${!isActive ? 'line-through' : ''}`}
          >
            {shownLabel}
          </button>
        )}
        {/* Chevron : ouvre éditeur séparé (seulement si canToggle, sinon le click sur valeur édite déjà) */}
        {canToggle && !isEditing && (
          <button
            type="button"
            onClick={openEditor}
            title={`${fullLabel} — changer la valeur`}
            className="p-0.5 hover:bg-black/5 rounded-full"
          >
            <ChevronDownIcon className="w-2.5 h-2.5 opacity-60" />
          </button>
        )}
      </div>
      {/* X au hover (si retirable) */}
      {canRemove && onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 w-3.5 h-3.5 flex items-center justify-center text-gray-400 hover:text-violet-700 opacity-0 group-hover:opacity-100 transition-opacity"
          title={`Retirer « ${fullLabel} »`}
        >
          <XMarkIcon className="w-2.5 h-2.5" />
        </button>
      )}

      {/* Dropdown pour enum */}
      {isDropdownOpen && type === 'enum' && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px]">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onValueChange(opt.value); setIsDropdownOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-violet-50 hover:text-violet-700 flex items-center gap-2 ${opt.value === value ? 'text-violet-700 font-medium' : 'text-gray-700'}`}
            >
              <span className={`w-3 ${opt.value === value ? 'opacity-100' : 'opacity-0'}`}>✓</span>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AddProgChipButton — bouton "Ajouter" avec dropdown des paramètres disponibles
// (pattern AddParamButton de Chiffrage)
// ---------------------------------------------------------------------------

interface AddProgChipButtonProps {
  available: OptionalParamDef[];
  onAdd: (key: OptionalParamKey) => void;
}

const AddProgChipButton: React.FC<AddProgChipButtonProps> = ({ available, onAdd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-400 hover:text-violet-600 hover:bg-violet-50 border border-dashed border-gray-300 hover:border-violet-300 rounded-full transition-colors"
      >
        <PlusIcon className="w-3 h-3" />
        Ajouter
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[200px]">
          {available.map(def => (
            <button
              key={def.key}
              type="button"
              onClick={() => { onAdd(def.key); setIsOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-violet-50 hover:text-violet-700"
            >
              {def.fullLabel}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Onglet 1 — Estimation travaux
// ---------------------------------------------------------------------------

interface EstimationTabProps {
  programme: Programme;
  bilan: BilanCompute;
  baseTravaux: number;
  activeOptional: Set<OptionalParamKey>;
  lotOverrides: Map<string, LotOverride>;
  onUpdateOverride: (categorie: string, partial: Partial<LotOverride>) => void;
  onResetOverride: (categorie: string) => void;
  onResetAll: () => void;
}

type LotView = 'famille' | 'lot';

const EstimationTab: React.FC<EstimationTabProps> = ({
  programme,
  bilan,
  baseTravaux,
  activeOptional,
  lotOverrides,
  onUpdateOverride,
  onResetOverride,
  onResetAll,
}) => {
  const [view, setView] = useState<LotView>('lot');
  const [expandedEditor, setExpandedEditor] = useState<Set<string>>(new Set());
  // Mode Précis : révèle les variantes matériaux par lot (Excel-like).
  // Express = ratio médian par défaut. Précis = choix matériau qui override le ratio.
  const [modePrecis, setModePrecis] = useState(false);

  // Fourchette du marché : ratio de base × coef cumulé (les filtres actifs s'appliquent)
  const coefCumule = getCoefCumule(programme, activeOptional);
  const coefBreakdown = getCoefBreakdown(programme, activeOptional);
  const travauxMin = BASE_RATIO_FOURCHETTE.min * coefCumule * programme.sdp;
  const travauxMax = BASE_RATIO_FOURCHETTE.max * coefCumule * programme.sdp;
  const coutParLogt = programme.nbLogements > 0 ? bilan.travaux / programme.nbLogements : 0;
  const globalFiabilite = fiabiliteFromN(GLOBAL_NB_COMPARABLES);
  const ecartVsMarche = bilan.travaux - baseTravaux;
  const ecartPct = baseTravaux > 0 ? ecartVsMarche / baseTravaux : 0;
  const nbExcluded = REPARTITION_LOTS.filter(l => lotOverrides.get(l.categorie)?.excluded).length;
  const nbCustomized = REPARTITION_LOTS.filter(l => {
    const ov = lotOverrides.get(l.categorie);
    return ov !== undefined && !ov.excluded && ov.mode !== 'auto';
  }).length;

  const toggleEditor = (categorie: string) => {
    setExpandedEditor(prev => {
      const next = new Set(prev);
      if (next.has(categorie)) next.delete(categorie);
      else next.add(categorie);
      return next;
    });
  };

  return (
    <>
      {/* Total dominant en haut */}
      <section className="mb-6">
        <div className="bg-white shadow rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            {/* Total principal (gauche) */}
            <div className="md:col-span-6 md:border-r md:border-gray-200 md:pr-6">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-sm font-medium text-gray-500">Coût travaux estimé (médian)</div>
                {(nbExcluded > 0 || nbCustomized > 0) && (
                  <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-semibold">
                    Personnalisé
                  </span>
                )}
              </div>
              <div className="text-5xl font-bold text-violet-700 leading-none">{formatEuroM(bilan.travaux)}</div>
              <div className="text-sm text-gray-600 mt-3">
                Fourchette <span className="font-medium text-gray-700">{formatEuroM(travauxMin)}</span>
                {' — '}
                <span className="font-medium text-gray-700">{formatEuroM(travauxMax)}</span>
              </div>
              {/* Écart vs marché si overrides */}
              {(nbExcluded > 0 || nbCustomized > 0) && Math.abs(ecartVsMarche) > 1 && (
                <div className={`text-xs mt-2 flex items-center gap-1 ${ecartVsMarche >= 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {ecartVsMarche >= 0 ? <ArrowTrendingUpIcon className="w-3.5 h-3.5" /> : <ArrowTrendingDownIcon className="w-3.5 h-3.5" />}
                  {ecartVsMarche >= 0 ? '+' : ''}{formatEuroM(ecartVsMarche)} ({ecartVsMarche >= 0 ? '+' : ''}{formatPct(ecartPct)}) vs marché
                </div>
              )}
            </div>
            {/* Stats secondaires (droite) */}
            <div className="md:col-span-6 grid grid-cols-3 gap-4">
              <RatioMedianStatWithBreakdown
                ratio={bilan.travaux / Math.max(programme.sdp, 1)}
                breakdown={coefBreakdown}
                base={BASE_RATIO_FOURCHETTE.median}
                sdp={programme.sdp}
              />
              <CompactStat
                label="Surface"
                value={`${formatInt(programme.sdp)} m²`}
                color="blue"
                hint="SDP"
              />
              <CompactStat
                label="Par logement"
                value={formatEuro(coutParLogt)}
                color="green"
                hint={`${programme.nbLogements} logts`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Source des données globale */}
      <section className="mb-4">
        <div className={`rounded-lg border p-3 flex items-center justify-between gap-4 ${
          globalFiabilite === 'limitee' ? 'bg-orange-50 border-orange-200' :
          globalFiabilite === 'normale' ? 'bg-violet-50 border-violet-200' :
                                          'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center gap-3">
            <CircleStackIcon className={`w-5 h-5 ${
              globalFiabilite === 'limitee' ? 'text-orange-600' :
              globalFiabilite === 'normale' ? 'text-violet-600' :
                                              'text-green-600'
            }`} />
            <div className="text-sm">
              <span className="font-semibold text-gray-900">
                Basé sur {GLOBAL_NB_COMPARABLES} projets comparables
              </span>
              <span className="text-gray-600">
                {' · '}{programme.ville}
                {activeOptional.has('typologie') ? ` · ${programme.typologie}` : ' · toutes typologies'}
                {activeOptional.has('gamme')     ? ` · ${programme.gamme}`     : ' · toutes gammes'}
                {' · 2023-2025'}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="text-xs font-medium text-violet-700 hover:text-violet-900 underline"
          >
            Voir les projets sources
          </button>
        </div>
        {globalFiabilite === 'limitee' && (
          <div className="mt-2 text-xs text-orange-700 flex items-center gap-1">
            <ExclamationTriangleIcon className="w-4 h-4" />
            Données limitées — élargissez les filtres du programme pour gagner en fiabilité.
          </div>
        )}
      </section>

      {/* Répartition par lot/famille */}
      <section className="bg-white shadow rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Répartition par {view === 'famille' ? 'famille' : 'lot'}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {modePrecis
                ? "Mode Précis : choisissez un matériau par lot (couverture, façades, finitions, CVC). Le ratio s'ajuste en cascade."
                : 'Excluez un lot ou personnalisez son montant — le total et les onglets suivants se mettent à jour.'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Reset all si overrides actifs */}
            {(nbExcluded > 0 || nbCustomized > 0) && (
              <button
                type="button"
                onClick={onResetAll}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                Réinitialiser ({nbExcluded + nbCustomized})
              </button>
            )}
            {/* Toggle Express / Précis */}
            <div className="bg-gray-100 rounded-lg p-0.5 flex" title={modePrecis ? 'Mode Précis activé : matériaux par lot' : 'Mode Express : ratios marché'}>
              <button
                type="button"
                onClick={() => setModePrecis(false)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  !modePrecis ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Express
              </button>
              <button
                type="button"
                onClick={() => setModePrecis(true)}
                className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  modePrecis ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Précis
                <span className={`inline-flex items-center text-[9px] font-semibold px-1 py-0.5 rounded ${
                  modePrecis ? 'bg-violet-100 text-violet-700' : 'bg-gray-200 text-gray-500'
                }`}>BÊTA</span>
              </button>
            </div>
            {/* Toggle Famille / Lot */}
            <div className="bg-gray-100 rounded-lg p-0.5 flex">
              <button
                type="button"
                onClick={() => setView('famille')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  view === 'famille' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Par famille
              </button>
              <button
                type="button"
                onClick={() => setView('lot')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  view === 'lot' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Par lot
              </button>
            </div>
          </div>
        </div>

        {/* Bandeau d'info Mode Précis */}
        {modePrecis && view === 'lot' && (
          <div className="bg-violet-50/60 border-b border-violet-100 px-6 py-2 text-xs text-violet-800 flex items-center gap-2">
            <SparklesIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              Variantes matériaux disponibles sur les <strong>{Object.keys(LOT_VARIANTES).length} lots</strong>.
              Choisissez votre matériau via le sélecteur ✨ sur la ligne du lot — le coût travaux se met à jour en cascade.
            </span>
          </div>
        )}

        {/* Vue par famille (agrégée, non-éditable au niveau lot) */}
        {view === 'famille' && (
          <div className="divide-y divide-gray-100">
            {FAMILLES_ORDER.map(famille => {
              const lotsFamille = REPARTITION_LOTS.filter(l => l.famille === famille);
              if (lotsFamille.length === 0) return null;
              const totalFamille = lotsFamille.reduce(
                (s, l) => s + getEffectiveLotCout(l, lotOverrides.get(l.categorie), baseTravaux, programme),
                0,
              );
              const pctFamille = lotsFamille.reduce((s, l) => s + l.pct, 0);
              const minFamille = travauxMin * pctFamille;
              const maxFamille = travauxMax * pctFamille;
              const baseFamille = baseTravaux * pctFamille;
              const p25Famille = (minFamille + baseFamille) / 2;
              const p75Famille = (baseFamille + maxFamille) / 2;
              const ratioFamille = totalFamille / Math.max(programme.sdp, 1);
              const lotsExcludedInFamille = lotsFamille.filter(l => lotOverrides.get(l.categorie)?.excluded).length;
              const lotsCustomizedInFamille = lotsFamille.filter(l => {
                const ov = lotOverrides.get(l.categorie);
                return ov !== undefined && !ov.excluded && ov.mode !== 'auto';
              }).length;
              return (
                <div key={famille} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-gray-900">{famille}</span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                        {lotsFamille.length} lot{lotsFamille.length > 1 ? 's' : ''} · {formatPct(pctFamille)}
                      </span>
                      {(lotsExcludedInFamille > 0 || lotsCustomizedInFamille > 0) && (
                        <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-medium">
                          Personnalisé
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Ratio</div>
                        <div className="text-sm font-semibold text-violet-600">{formatInt(ratioFamille)} €/m²</div>
                      </div>
                      <div className="text-right min-w-[120px]">
                        <div className="text-xs text-gray-500">Coût</div>
                        <div className="text-sm font-semibold text-gray-900">{formatEuro(totalFamille)}</div>
                      </div>
                    </div>
                  </div>
                  <RangeBar min={minFamille} max={maxFamille} p25={p25Famille} median={baseFamille} p75={p75Famille} />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{formatEuro(minFamille)}</span>
                    <span>{formatEuro(maxFamille)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Vue par lot (éditable) */}
        {view === 'lot' && (
          <div className="divide-y divide-gray-100">
            {REPARTITION_LOTS.map(lot => (
              <LotRow
                key={lot.categorie}
                lot={lot}
                override={lotOverrides.get(lot.categorie)}
                programme={programme}
                baseTravaux={baseTravaux}
                travauxMin={travauxMin}
                travauxMax={travauxMax}
                editorOpen={expandedEditor.has(lot.categorie)}
                onToggleEditor={() => toggleEditor(lot.categorie)}
                onUpdateOverride={(partial) => onUpdateOverride(lot.categorie, partial)}
                onResetOverride={() => onResetOverride(lot.categorie)}
                modePrecis={modePrecis}
                variantes={LOT_VARIANTES[lot.categorie]}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// LotRow — une ligne de lot avec exclusion + override inline
// ---------------------------------------------------------------------------

interface LotRowProps {
  lot: LotConfig;
  override: LotOverride | undefined;
  programme: Programme;
  baseTravaux: number;
  travauxMin: number;
  travauxMax: number;
  editorOpen: boolean;
  onToggleEditor: () => void;
  onUpdateOverride: (partial: Partial<LotOverride>) => void;
  onResetOverride: () => void;
  modePrecis?: boolean;             // si true ET variantes présentes → afficher le sélecteur matériau
  variantes?: LotVariante[];        // variantes disponibles pour ce lot (sinon undefined)
}

const LotRow: React.FC<LotRowProps> = ({
  lot, override, programme, baseTravaux, travauxMin, travauxMax,
  editorOpen, onToggleEditor, onUpdateOverride, onResetOverride,
  modePrecis = false, variantes,
}) => {
  const ov = override ?? DEFAULT_OVERRIDE;
  const excluded = ov.excluded;
  const customized = !excluded && ov.mode !== 'auto';
  const autoCout = baseTravaux * lot.pct;
  const effectiveCout = getEffectiveLotCout(lot, override, baseTravaux, programme);
  const coutMin = travauxMin * lot.pct;
  const coutMax = travauxMax * lot.pct;
  const coutP25 = (coutMin + autoCout) / 2;
  const coutP75 = (autoCout + coutMax) / 2;
  const ratio = effectiveCout / Math.max(programme.sdp, 1);
  const fiabilite = fiabiliteFromN(lot.nbComparables);

  return (
    <div className={`px-6 py-4 transition-colors ${
      excluded ? 'bg-gray-50 opacity-60' :
      customized ? 'border-l-4 border-violet-500 bg-violet-50/30' :
      'hover:bg-gray-50'
    }`}>
      <div className="flex items-center justify-between mb-2 gap-3">
        {/* Gauche : checkbox + nom + badges */}
        <div className="flex items-center gap-3 min-w-0">
          <input
            type="checkbox"
            checked={!excluded}
            onChange={(e) => onUpdateOverride({ excluded: !e.target.checked })}
            className="w-4 h-4 accent-violet-600 cursor-pointer"
            title={excluded ? 'Inclure ce lot' : 'Exclure ce lot'}
          />
          <span className={`text-sm font-medium ${excluded ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
            {lot.categorie}
          </span>
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
            {formatPct(lot.pct)}
          </span>
          <FiabiliteBadge fiabilite={fiabilite} nbComparables={lot.nbComparables} />
          {customized && (
            <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-medium">
              {ov.mode === 'ratio' ? 'Ratio fixé' : 'Montant fixé'}
            </span>
          )}
          {/* Sélecteur de variante matériau (Mode Précis uniquement) */}
          {modePrecis && variantes && !excluded && (
            <VarianteSelector
              variantes={variantes}
              currentRatio={ratio}
              onSelect={(v) => onUpdateOverride({ mode: 'ratio', ratio: v.ratio })}
              onReset={onResetOverride}
            />
          )}
        </div>

        {/* Droite : ratio + coût + bouton Personnaliser */}
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-xs text-gray-500">Ratio</div>
            <div className={`text-sm font-semibold ${excluded ? 'text-gray-400' : 'text-violet-600'}`}>
              {excluded ? '—' : `${formatInt(ratio)} €/m²`}
            </div>
          </div>
          <div className="text-right min-w-[120px]">
            <div className="text-xs text-gray-500">Coût</div>
            <div className={`text-sm font-semibold ${excluded ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
              {formatEuro(effectiveCout)}
            </div>
          </div>
          <button
            type="button"
            onClick={onToggleEditor}
            disabled={excluded}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
              excluded ? 'text-gray-300 cursor-not-allowed' :
              editorOpen ? 'bg-violet-100 text-violet-700' :
              'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
            Personnaliser
            {editorOpen ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Range bar — masquée si exclu */}
      {!excluded && (
        <>
          <RangeBar min={coutMin} max={coutMax} p25={coutP25} median={autoCout} p75={coutP75} />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{formatEuro(coutMin)}</span>
            <span>{formatEuro(coutMax)}</span>
          </div>
        </>
      )}

      {/* Editor de personnalisation */}
      {editorOpen && !excluded && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <OverrideEditor
            mode={ov.mode}
            ratio={ov.ratio}
            amount={ov.amount}
            autoRatio={autoCout / Math.max(programme.sdp, 1)}
            autoCout={autoCout}
            sdp={programme.sdp}
            isCustomized={customized}
            onChange={onUpdateOverride}
            onReset={onResetOverride}
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// OverrideEditor — mini éditeur inline pour customiser un lot
// ---------------------------------------------------------------------------

interface OverrideEditorProps {
  mode: LotOverrideMode;
  ratio?: number;
  amount?: number;
  autoRatio: number;
  autoCout: number;
  sdp: number;
  isCustomized: boolean;
  onChange: (partial: Partial<LotOverride>) => void;
  onReset: () => void;
}

const OverrideEditor: React.FC<OverrideEditorProps> = ({
  mode, ratio, amount, autoRatio, autoCout, sdp, isCustomized, onChange, onReset,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">Mode de valorisation</span>
        {isCustomized && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-900"
          >
            <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
            Revenir au marché
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <ModeButton active={mode === 'auto'}   onClick={() => onChange({ mode: 'auto' })}>
          Auto (marché)
        </ModeButton>
        <ModeButton active={mode === 'ratio'}  onClick={() => onChange({ mode: 'ratio', ratio: ratio ?? Math.round(autoRatio) })}>
          Ratio €/m² fixé
        </ModeButton>
        <ModeButton active={mode === 'amount'} onClick={() => onChange({ mode: 'amount', amount: amount ?? Math.round(autoCout) })}>
          Montant fixé
        </ModeButton>
      </div>
      {mode === 'auto' && (
        <div className="text-xs text-gray-500">
          Utilise le ratio médian du marché : <span className="font-semibold text-gray-700">{formatInt(autoRatio)} €/m²</span> ({formatEuro(autoCout)}).
        </div>
      )}
      {mode === 'ratio' && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={ratio ?? ''}
            onChange={(e) => onChange({ ratio: Number(e.target.value) || 0 })}
            placeholder={`${formatInt(autoRatio)}`}
            className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <span className="text-sm text-gray-600">€/m² SDP</span>
          <span className="text-xs text-gray-400 ml-2">
            = {formatEuro((ratio ?? 0) * sdp)} pour {formatInt(sdp)} m²
          </span>
        </div>
      )}
      {mode === 'amount' && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={amount ?? ''}
            onChange={(e) => onChange({ amount: Number(e.target.value) || 0 })}
            placeholder={`${formatInt(autoCout)}`}
            className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <span className="text-sm text-gray-600">€ (forfait)</span>
          <span className="text-xs text-gray-400 ml-2">
            = {formatInt((amount ?? 0) / Math.max(sdp, 1))} €/m²
          </span>
        </div>
      )}
    </div>
  );
};

const ModeButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
      active ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// RatioMedianStatWithBreakdown — CompactStat avec icône "?" qui révèle au hover
// la composition détaillée du ratio (waterfall des coefs actifs)
// ---------------------------------------------------------------------------

interface RatioMedianStatWithBreakdownProps {
  ratio: number;
  base: number;
  breakdown: Array<{ label: string; coef: number }>;
  sdp: number;
}

const RatioMedianStatWithBreakdown: React.FC<RatioMedianStatWithBreakdownProps> = ({ ratio, base, breakdown, sdp }) => {
  return (
    <div className="text-center min-w-0 relative">
      <div className="text-xl font-bold whitespace-nowrap text-violet-600">
        {formatInt(ratio)} €/m²
      </div>
      <div className="text-xs text-gray-600 mt-0.5 inline-flex items-center gap-1">
        Ratio médian
        {/* Le tooltip est attaché AU SEUL hover de l'icône (groupe nommé /qmark) */}
        <span className="relative group/qmark inline-flex items-center text-gray-400 hover:text-violet-600 cursor-help" title="Voir la composition du ratio">
          <QuestionMarkCircleIcon className="w-3.5 h-3.5" />
          <div
            role="tooltip"
            className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[420px] max-w-[90vw] z-50 opacity-0 group-hover/qmark:opacity-100 transition-opacity duration-150 delay-100"
          >
            <CompositionWaterfall base={base} breakdown={breakdown} sdp={sdp} />
          </div>
        </span>
      </div>
      <div className="text-[10px] text-gray-400 mt-0.5 truncate">
        {breakdown.length > 0 ? breakdown.map(c => c.label).join(' · ') : 'Aucun filtre actif'}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CompositionWaterfall — visualise la composition du ratio
// Affiché en tooltip flottant au hover du "?" sur la stat "Ratio médian"
// ---------------------------------------------------------------------------

interface CompositionWaterfallProps {
  base: number;
  breakdown: Array<{ label: string; coef: number }>;
  sdp: number;
}

const CompositionWaterfall: React.FC<CompositionWaterfallProps> = ({ base, breakdown, sdp }) => {
  // Calcul cumulatif : chaque step part du ratio précédent et applique son coef
  let running = base;
  const steps = breakdown.map(c => {
    const before = running;
    const after = before * c.coef;
    const contribution = after - before;
    running = after;
    return { label: c.label, coef: c.coef, contribution, after };
  });
  const totalFinal = running;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-4 text-left">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-3">
        Composition du ratio (€/m² SDP)
        <div className="normal-case text-gray-400 font-normal text-[10px] mt-0.5">Un filtre absent du bandeau n'est pas imputé</div>
      </div>
      <div className="space-y-1">
        <CompositionRow label="Base marché (tout-venant)" value={base} isBase totalEur={base * sdp} />
        {steps.map((s, i) => (
          <CompositionRow
            key={`${s.label}-${i}`}
            label={s.label}
            value={s.after}
            coef={s.coef}
            contribution={s.contribution}
            totalEur={s.after * sdp}
          />
        ))}
        <div className="flex items-center justify-between pt-2 mt-1 border-t border-dashed border-gray-200">
          <span className="text-xs font-semibold text-violet-700">Ratio retenu</span>
          <div className="flex items-center gap-3">
            <span className="text-xs tabular-nums font-bold text-violet-700">{formatInt(totalFinal)} €/m²</span>
            <span className="text-xs tabular-nums text-gray-500 min-w-[80px] text-right">= {formatEuroM(totalFinal * sdp)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CompositionRowProps {
  label: string;
  value: number;
  coef?: number;
  contribution?: number;
  isBase?: boolean;
  totalEur: number;
}

const CompositionRow: React.FC<CompositionRowProps> = ({ label, value, coef, contribution, isBase, totalEur }) => {
  const contribColor =
    contribution === undefined || contribution === 0 ? 'text-gray-400' :
    contribution > 0 ? 'text-orange-600' :
                       'text-green-600';
  const sign = contribution !== undefined && contribution > 0 ? '+' : contribution !== undefined && contribution < 0 ? '−' : '';
  return (
    <div className="flex items-center justify-between text-xs">
      <span className={isBase ? 'font-medium text-gray-700' : 'text-gray-600'}>{label}</span>
      <div className="flex items-center gap-3">
        {coef !== undefined && (
          <span className="tabular-nums text-gray-400 w-12 text-right">× {coef.toFixed(2)}</span>
        )}
        {!isBase && (
          <span className={`tabular-nums font-medium w-16 text-right ${contribColor}`}>
            {contribution !== undefined && Math.abs(contribution) >= 0.5
              ? `${sign}${formatInt(Math.abs(contribution))} €`
              : '—'}
          </span>
        )}
        {isBase && <span className="w-16" />}
        {!coef && !isBase && <span className="w-12" />}
        <span className="tabular-nums font-semibold text-gray-900 w-20 text-right">{formatInt(value)} €/m²</span>
        <span className="tabular-nums text-gray-400 w-20 text-right">{formatEuroM(totalEur)}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FiabiliteBadge — pastille indiquant la fiabilité des données pour un lot
// ---------------------------------------------------------------------------

const FiabiliteBadge: React.FC<{ fiabilite: Fiabilite; nbComparables: number }> = ({ fiabilite, nbComparables }) => {
  const map: Record<Fiabilite, { cls: string; label: string }> = {
    haute:   { cls: 'bg-green-100 text-green-700',   label: `${nbComparables} projets` },
    normale: { cls: 'bg-violet-100 text-violet-700', label: `${nbComparables} projets` },
    limitee: { cls: 'bg-orange-100 text-orange-700', label: `${nbComparables} projets · limité` },
  };
  const { cls, label } = map[fiabilite];
  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}
      title={`Fiabilité ${fiabilite} — ${nbComparables} projets comparables`}
    >
      {fiabilite === 'limitee' && <ExclamationTriangleIcon className="w-3 h-3 mr-1" />}
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Onglet 2 — Bilan d'opération (waterfall)
// ---------------------------------------------------------------------------

interface BilanTabProps {
  programme: Programme;
  bilan: BilanCompute;
  abaque: AbaqueRatios;
  hasOverrides: boolean;
  activeOptional: Set<OptionalParamKey>;
  onActivatePhase: () => void;
  // State remonté au parent — BilanTab est maintenant contrôlé.
  localPcts: LocalPcts;
  setPct: (key: keyof LocalPcts, v: number) => void;
  chargeFonciereOverride: number | null;
  setChargeFonciereOverride: (v: number | null) => void;
  // Valeurs effectives calculées au parent (source de vérité partagée avec le KPI strip)
  honorairesEff: number;
  fraisAnnexesEff: number;
  fraisFinanciersEff: number;
  fraisCommerciauxEff: number;
  totalHorsTerrainEff: number;
  margeCibleEff: number;
  margeCiblePctEff: number;
  chargeFonciereImpliciteEff: number;
}

const BilanTab: React.FC<BilanTabProps> = ({
  programme,
  bilan,
  abaque,
  hasOverrides,
  activeOptional,
  onActivatePhase,
  localPcts,
  setPct,
  chargeFonciereOverride,
  setChargeFonciereOverride,
  honorairesEff,
  fraisAnnexesEff,
  fraisFinanciersEff,
  fraisCommerciauxEff,
  totalHorsTerrainEff,
  margeCibleEff,
  margeCiblePctEff,
  chargeFonciereImpliciteEff,
}) => {
  const aleas = bilan.aleas; // déjà calculé avec la phase active

  // Décomposition de la charge foncière en prix d'achat + frais (si frais d'acquisition actif)
  const isFraisActive = activeOptional.has('fraisAcquisitionPct');
  const { prixAchat, frais, tauxFrais } = decomposerCharge(chargeFonciereImpliciteEff, programme, activeOptional);
  const isTVAActive = activeOptional.has('regimeTVA');
  const isPhaseActive = activeOptional.has('phaseProjet');
  const aleasPctEff = getEffectiveAleasPct(programme, abaque, activeOptional);

  // Construction dynamique du waterfall — sub-text en ReactNode (éditable pour les % / non éditable pour les autres)
  type Ligne = { label: string; value: number; type: 'positif' | 'negatif' | 'total' | 'marge'; sub?: React.ReactNode };
  const lignes: Ligne[] = [
    { label: 'Prix de vente HT (CA)', value: bilan.caHt, type: 'positif', sub: `${formatInt(programme.pvCible)} €/m² × ${formatInt(programme.sdp)} m²${isTVAActive ? ` · TTC ${formatEuroM(bilan.caTtc)}` : ''}` },
    ...(isFraisActive ? [
      { label: "Prix d'achat terrain", value: -prixAchat, type: 'negatif' as const,
        sub: <EditableEurSub value={prixAchat} onChange={(v) => setChargeFonciereOverride(v * (1 + tauxFrais))} suffix="payé au vendeur" />,
      },
      { label: "Frais d'acquisition (notaire + droits)", value: -frais, type: 'negatif' as const, sub: `${formatPct(tauxFrais)} du prix d'achat` },
    ] : [
      { label: 'Charge foncière (implicite)', value: -chargeFonciereImpliciteEff, type: 'negatif' as const,
        sub: <EditableEurSub value={chargeFonciereImpliciteEff} onChange={setChargeFonciereOverride} suffix={chargeFonciereOverride !== null ? '(montant fixé — la marge se recalcule)' : 'calculé pour atteindre la marge cible'} />,
      },
    ]),
    { label: 'Coût travaux', value: -bilan.travaux, type: 'negatif', sub: `${formatInt(bilan.travaux / Math.max(programme.sdp, 1))} €/m²` },
    { label: 'Honoraires (MOE, BET, CT, SPS)', value: -honorairesEff, type: 'negatif',
      sub: <EditablePctSub pct={localPcts.honoraires} onChange={(v) => setPct('honoraires', v)} suffix=" du coût travaux" />,
    },
    { label: 'Frais annexes', value: -fraisAnnexesEff, type: 'negatif',
      sub: <EditablePctSub pct={localPcts.fraisAnnexes} onChange={(v) => setPct('fraisAnnexes', v)} suffix=" du CA" detail=" — assurances, taxes, raccordements" />,
    },
    { label: 'Frais financiers', value: -fraisFinanciersEff, type: 'negatif',
      sub: <EditablePctSub pct={localPcts.fraisFinanciers} onChange={(v) => setPct('fraisFinanciers', v)} suffix=" du CA" detail=" — portage + crédit construction" />,
    },
    { label: 'Frais commerciaux', value: -fraisCommerciauxEff, type: 'negatif',
      sub: <EditablePctSub pct={localPcts.fraisCommerciaux} onChange={(v) => setPct('fraisCommerciaux', v)} suffix=" du CA" detail=" — commercialisation" />,
    },
    { label: 'Aléas', value: -aleas, type: 'negatif',
      sub: <AleasSub pct={aleasPctEff} isPhaseActive={isPhaseActive} phaseLabel={programme.phaseProjet} onActivatePhase={onActivatePhase} />,
    },
    { label: 'Marge prévisionnelle', value: margeCibleEff, type: 'marge',
      sub: <EditablePctSub pct={margeCiblePctEff} onChange={(v) => setPct('margeCible', v)} suffix=" du CA HT" detail={chargeFonciereOverride !== null ? ' (recalculé — charge foncière fixée)' : ' (cible)'} />,
    },
  ];

  return (
    <>
      {/* Notice overrides */}
      {hasOverrides && (
        <section className="mb-4">
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex items-center gap-2 text-sm text-violet-800">
            <AdjustmentsHorizontalIcon className="w-4 h-4 flex-shrink-0" />
            Les chiffres ci-dessous intègrent vos personnalisations de lots (vue Estimation).
          </div>
        </section>
      )}

      {/* KPI bar */}
      <section className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard label={isTVAActive ? 'CA HT total' : 'CA total'}
                   value={formatEuroM(bilan.caHt)}
                   color="blue"
                   hint={isTVAActive ? `TTC ${formatEuroM(bilan.caTtc)} (TVA ${(bilan.tauxTVA * 100).toFixed(bilan.tauxTVA < 0.1 ? 1 : 0)} %)` : undefined} />
          <KpiCard label="Prix de revient"     value={formatEuroM(totalHorsTerrainEff + chargeFonciereImpliciteEff)} color="orange" />
          <KpiCard label="Marge prévisionnelle" value={formatEuroM(margeCibleEff)}             color="green"  hint={formatPct(margeCiblePctEff)} />
          {isFraisActive ? (
            <KpiCard label="Prix d'achat terrain max"
                     value={formatEuroM(prixAchat)}
                     color="violet"
                     hint={`${formatInt(prixAchat / programme.nbLogements)} €/logt · +${formatEuro(frais)} de frais`} />
          ) : (
            <KpiCard label="Charge foncière implicite"
                     value={formatEuroM(chargeFonciereImpliciteEff)}
                     color="violet"
                     hint={`${formatInt(chargeFonciereImpliciteEff / programme.nbLogements)} €/logt`} />
          )}
        </div>
      </section>

      {/* Waterfall */}
      <section className="bg-white shadow rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Décomposition du bilan d'opération</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Cascade des postes pour atteindre la marge cible — modifiez le programme ci-dessus pour recalculer en direct
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {lignes.map((ligne) => (
            <WaterfallRow key={ligne.label} {...ligne} />
          ))}
        </div>
      </section>

      {/* Note */}
      <section className="mt-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Les ratios d'honoraires et de frais sont des abaques marché</p>
            <p className="text-blue-700">
              Vous pourrez les ajuster opération par opération (à venir). La charge foncière affichée ici est <em>implicite</em> :
              c'est ce que vous pouvez payer pour atteindre la marge cible. L'onglet <strong>Charge foncière max</strong> permet de
              jouer sur cette cible.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// Onglet 3 — Charge foncière max (calcul rétro avec sliders)
// ---------------------------------------------------------------------------

interface ChargeFonciereTabProps {
  programme: Programme;
  bilan: BilanCompute;
  hasOverrides: boolean;
  activeOptional: Set<OptionalParamKey>;
  // Source de vérité partagée avec le Bilan — slider marge bidirectionnel,
  // honoraires/frais reflètent les édits du waterfall.
  localPcts: LocalPcts;
  setPct: (key: keyof LocalPcts, v: number) => void;
}

const ChargeFonciereTab: React.FC<ChargeFonciereTabProps> = ({ programme, bilan, hasOverrides, activeOptional, localPcts, setPct }) => {
  const [pvAjuste, setPvAjuste] = useState(programme.pvCible);
  const [travauxAjustePct, setTravauxAjustePct] = useState(1.0); // multiplicateur
  const [prixDemande, setPrixDemande] = useState(5_800_000);
  // Provision pour risques (saisie libre en €) — déduite de la charge foncière max
  const [provision, setProvision] = useState(0);

  // Recalcul avec les ajustements — honoraires et frais utilisent les % édités côté Bilan
  const margePct = localPcts.margeCible;
  const caAjuste = pvAjuste * programme.sdp;
  const travauxAjuste = bilan.travaux * travauxAjustePct;
  const aleasAjuste = bilan.aleas * travauxAjustePct;
  const honorairesAjuste = localPcts.honoraires * travauxAjuste;
  const fraisAjustes = caAjuste * (localPcts.fraisAnnexes + localPcts.fraisFinanciers + localPcts.fraisCommerciaux);
  const totalCouts = travauxAjuste + honorairesAjuste + fraisAjustes + aleasAjuste;
  const margeCibleEur = margePct * caAjuste;
  // Charge foncière TOTALE admissible (= ce qu'on peut payer au total, incluant frais d'acquisition)
  const chargeFonciereMax = caAjuste - totalCouts - margeCibleEur - provision;

  // Décomposition prix d'achat / frais (si paramètre actif). Sinon prixAchatMax = chargeFonciereMax.
  const isFraisActive = activeOptional.has('fraisAcquisitionPct');
  const { prixAchat: prixAchatMax, frais: fraisAcqMax, tauxFrais } = decomposerCharge(chargeFonciereMax, programme, activeOptional);

  // L'écart se mesure sur le PRIX D'ACHAT (= ce qu'on paye au vendeur), pas la charge totale
  const ecart = prixAchatMax - prixDemande;
  const ecartPct = prixDemande > 0 ? ecart / prixDemande : 0;
  const dansLaCible = ecart >= 0;

  // Stress test (intègre aussi la provision + les frais d'acquisition réels sur le prix demandé)
  const fraisStress = isFraisActive ? prixDemande * tauxFrais : 0;
  const stressMargePct = (caAjuste * 0.95 - bilan.travaux * 1.08 - localPcts.honoraires * bilan.travaux * 1.08 - fraisAjustes - bilan.aleas * 1.08 - prixDemande - fraisStress - provision) / (caAjuste * 0.95);

  return (
    <>
      {/* Notice overrides */}
      {hasOverrides && (
        <section className="mb-4">
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 flex items-center gap-2 text-sm text-violet-800">
            <AdjustmentsHorizontalIcon className="w-4 h-4 flex-shrink-0" />
            Le coût travaux utilisé intègre vos personnalisations de lots (vue Estimation).
          </div>
        </section>
      )}

      {/* Bandeau résultat principal */}
      <section className="mb-6">
        <div className={`rounded-xl p-8 border-2 ${dansLaCible ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div>
              <div className="text-sm font-medium text-gray-600 mb-1">
                {isFraisActive ? "Prix d'achat terrain max" : 'Charge foncière maximum admissible'}
              </div>
              <div className={`text-4xl font-bold ${dansLaCible ? 'text-green-700' : 'text-orange-700'}`}>
                {formatEuroM(prixAchatMax)}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {formatInt(prixAchatMax / programme.nbLogements)} €/logt · {formatInt(prixAchatMax / programme.sdp)} €/m² SDP
              </div>
              {isFraisActive && (
                <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200/60">
                  + {formatEuro(fraisAcqMax)} frais ({formatPct(tauxFrais)}) = <span className="font-medium text-gray-700">{formatEuroM(chargeFonciereMax)}</span> total
                </div>
              )}
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-gray-600 mb-1">Prix demandé par le vendeur</div>
              <input
                type="number"
                value={prixDemande}
                onChange={(e) => setPrixDemande(Number(e.target.value) || 0)}
                className="w-full px-4 py-3 text-2xl font-bold text-center text-gray-900 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <div className="text-xs text-gray-500 mt-1">Saisissez le prix de mise en vente</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-600 mb-1">Écart</div>
              <div className={`flex items-center gap-2 text-2xl font-bold ${dansLaCible ? 'text-green-700' : 'text-orange-700'}`}>
                {dansLaCible ? <ArrowTrendingUpIcon className="w-7 h-7" /> : <ArrowTrendingDownIcon className="w-7 h-7" />}
                {dansLaCible ? '+' : ''}{formatEuroM(ecart)}
              </div>
              <div className={`text-sm font-medium ${dansLaCible ? 'text-green-700' : 'text-orange-700'}`}>
                {dansLaCible
                  ? `Marge de négociation : ${formatPct(Math.abs(ecartPct))}`
                  : `Au-dessus de la cible : ${formatPct(Math.abs(ecartPct))}`}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sliders + saisie provision */}
      <section className="mb-6 bg-white shadow rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Ajustez les hypothèses</h3>
        <p className="text-sm text-gray-500 mb-5">Les sliders et la provision recalculent la charge foncière max en direct</p>

        <div className="space-y-6">
          <SliderRow
            label="Marge cible"
            value={margePct}
            min={0.04}
            max={0.15}
            step={0.005}
            displayValue={formatPct(margePct)}
            displayHint={`${formatEuroM(margeCibleEur)} — synchronisé avec le Bilan`}
            color="violet"
            onChange={(v) => setPct('margeCible', v)}
          />
          <SliderRow
            label="Prix de vente"
            value={pvAjuste}
            min={Math.round(programme.pvCible * 0.7)}
            max={Math.round(programme.pvCible * 1.3)}
            step={50}
            displayValue={`${formatInt(pvAjuste)} €/m²`}
            displayHint={`CA ${formatEuroM(caAjuste)}`}
            color="blue"
            onChange={setPvAjuste}
          />
          <SliderRow
            label="Coût travaux"
            value={travauxAjustePct}
            min={0.85}
            max={1.20}
            step={0.01}
            displayValue={`${travauxAjustePct > 1 ? '+' : ''}${formatPct(travauxAjustePct - 1)}`}
            displayHint={`${formatEuroM(travauxAjuste)} (${formatInt(travauxAjuste / programme.sdp)} €/m²)`}
            color="orange"
            onChange={setTravauxAjustePct}
          />
          {/* Séparateur visuel : la provision n'est pas un slider, elle est saisie en €  */}
          <div className="border-t border-dashed border-gray-200 pt-5">
            <InputRow
              label="Enveloppe provision"
              value={provision}
              unit="€"
              hint="Pour risques techniques, dépollution, archéologie, recours… Somme déduite de la charge foncière max."
              displayHint={provision > 0 && caAjuste > 0 ? `${formatEuroM(provision)} · ${formatPct(provision / caAjuste)} du CA` : 'Aucune provision'}
              color="amber"
              onChange={setProvision}
            />
          </div>
        </div>
      </section>

      {/* Stress test */}
      <section className="bg-white shadow rounded-xl p-6">
        <div className="flex items-start gap-3 mb-4">
          <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Stress test au prix demandé</h3>
            <p className="text-sm text-gray-500">Que se passe-t-il si vous achetez à {formatEuroM(prixDemande)} et que les ventes baissent de 5 % + travaux dérapent de 8 % ?</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Marge initiale visée</div>
            <div className="text-2xl font-bold text-gray-900">{formatPct(margePct)}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Marge stress test</div>
            <div className={`text-2xl font-bold ${stressMargePct > 0.03 ? 'text-green-700' : stressMargePct > 0 ? 'text-orange-600' : 'text-red-600'}`}>
              {formatPct(stressMargePct)}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">Résilience</div>
            <div className={`text-sm font-semibold ${stressMargePct > 0.03 ? 'text-green-700' : stressMargePct > 0 ? 'text-orange-600' : 'text-red-600'}`}>
              {stressMargePct > 0.03 ? 'Opération résiliente' : stressMargePct > 0 ? 'Marge fragilisée' : 'Opération en perte'}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// Onglet 4 — Scénarios (comparaison A vs B)
// ---------------------------------------------------------------------------

interface ScenariosTabProps {
  programme: Programme;
  abaque: AbaqueRatios;
  // % édités côté Bilan, propagés aux deux scénarios pour une comparaison
  // apples-to-apples des programmes à hypothèses financières constantes.
  localPcts: LocalPcts;
  activeOptional: Set<OptionalParamKey>;
}

const ScenariosTab: React.FC<ScenariosTabProps> = ({ programme, abaque, localPcts, activeOptional }) => {
  const [scenarioB, setScenarioB] = useState<Programme>({
    ...programme,
    nom: `${programme.nom} — Variante`,
    gamme: 'Haut de gamme',
    pvCible: programme.pvCible + 800,
  });

  // Abaque "utilisateur" — les 4 % éditables côté Bilan remplacent les défauts ;
  // aleas reste sur l'abaque (édité via la phase projet, pas via le waterfall).
  const userAbaque = useMemo<AbaqueRatios>(() => ({
    honoraires:       localPcts.honoraires,
    fraisAnnexes:     localPcts.fraisAnnexes,
    fraisFinanciers:  localPcts.fraisFinanciers,
    fraisCommerciaux: localPcts.fraisCommerciaux,
    aleas:            abaque.aleas,
  }), [localPcts, abaque]);

  const bilanA = useMemo(() => computeBilan(programme, userAbaque, activeOptional), [programme, userAbaque, activeOptional]);
  const bilanB = useMemo(() => computeBilan(scenarioB, userAbaque, activeOptional), [scenarioB, userAbaque, activeOptional]);

  // Marges implicites à la cible utilisateur (partagée avec le Bilan)
  const margePct = localPcts.margeCible;
  const cfImpliciteA = bilanA.caHt - bilanA.totalHorsTerrain - margePct * bilanA.caHt;
  const cfImpliciteB = bilanB.caHt - bilanB.totalHorsTerrain - margePct * bilanB.caHt;

  return (
    <>
      <section className="mb-6 bg-white shadow rounded-xl p-6">
        <div className="flex items-start gap-3">
          <ArrowsRightLeftIcon className="w-6 h-6 text-violet-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Comparer deux scénarios</h3>
            <p className="text-sm text-gray-500">
              Le scénario A reprend votre programme. Modifiez le scénario B pour tester une variante (gamme, prix de vente, surface, RE2025).
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ScenarioCard
          label="Scénario A — Référence"
          programme={programme}
          bilan={bilanA}
          chargeFonciereImplicite={cfImpliciteA}
          editable={false}
          accent="violet"
        />
        <ScenarioCard
          label="Scénario B — Variante"
          programme={scenarioB}
          bilan={bilanB}
          chargeFonciereImplicite={cfImpliciteB}
          editable={true}
          onChange={setScenarioB}
          accent="blue"
        />
      </div>

      {/* Synthèse écarts */}
      <section className="mt-6 bg-white shadow rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Synthèse des écarts (B vs A)</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <DeltaCard label="CA HT"             a={bilanA.caHt}             b={bilanB.caHt} />
          <DeltaCard label="Coût travaux"      a={bilanA.travaux}          b={bilanB.travaux} invertColor />
          <DeltaCard label="Charge foncière"   a={cfImpliciteA}            b={cfImpliciteB} />
          <DeltaCard label={`Marge cible ${formatPct(margePct)}`} a={margePct * bilanA.caHt} b={margePct * bilanB.caHt} />
        </div>
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// Composants utilitaires
// ---------------------------------------------------------------------------

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}
const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
      active
        ? 'border-violet-500 text-violet-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    <div className="flex items-center space-x-2">
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </div>
  </button>
);

interface FormFieldProps {
  label: string;
  className?: string;
  children: React.ReactNode;
}
const FormField: React.FC<FormFieldProps> = ({ label, className = '', children }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

interface SummaryStatProps {
  label: string;
  value: string;
  color: 'violet' | 'blue' | 'green' | 'orange';
  hint?: string;
}

// Version compacte pour les stats serrées sur 3 colonnes (évite les retours à la ligne)
const CompactStat: React.FC<SummaryStatProps> = ({ label, value, color, hint }) => {
  const colorMap = {
    violet: 'text-violet-600',
    blue:   'text-blue-600',
    green:  'text-green-600',
    orange: 'text-orange-600',
  };
  return (
    <div className="text-center min-w-0">
      <div className={`text-xl font-bold whitespace-nowrap ${colorMap[color]}`}>{value}</div>
      <div className="text-xs text-gray-600 mt-0.5">{label}</div>
      {hint && <div className="text-[10px] text-gray-400 mt-0.5 truncate">{hint}</div>}
    </div>
  );
};

interface KpiCardProps {
  label: string;
  value: string;
  color: 'violet' | 'blue' | 'green' | 'orange';
  hint?: string;
}
const KpiCard: React.FC<KpiCardProps> = ({ label, value, color, hint }) => {
  const colorMap = {
    violet: 'text-violet-700 border-violet-200 bg-violet-50',
    blue:   'text-blue-700 border-blue-200 bg-blue-50',
    green:  'text-green-700 border-green-200 bg-green-50',
    orange: 'text-orange-700 border-orange-200 bg-orange-50',
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color]}`}>
      <div className="text-xs font-medium opacity-75 mb-1">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {hint && <div className="text-xs opacity-75 mt-1">{hint}</div>}
    </div>
  );
};

interface RangeBarProps {
  min: number;
  max: number;
  p25: number;
  median: number;
  p75: number;
}
const RangeBar: React.FC<RangeBarProps> = ({ min, max, p25, median, p75 }) => {
  const span = max - min;
  const toPct = (v: number) => span > 0 ? Math.min(100, Math.max(0, ((v - min) / span) * 100)) : 50;
  const p25Pct = toPct(p25);
  const medPct = toPct(median);
  const p75Pct = toPct(p75);
  return (
    <div className="relative h-2 bg-gray-200 rounded-full">
      {/* Bande IQR (p25 → p75) */}
      <div
        className="absolute top-0 h-2 bg-violet-200 rounded-full"
        style={{ left: `${p25Pct}%`, width: `${Math.max(p75Pct - p25Pct, 2)}%` }}
      />
      {/* Trait médiane */}
      <div
        className="absolute w-1 h-4 -top-1 bg-violet-700 rounded"
        style={{ left: `${medPct}%`, transform: 'translateX(-50%)' }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// EditablePctSub — sub-text % cliquable qui devient input on-click
// Utilisé dans le waterfall Bilan pour rendre les % d'honoraires/frais éditables
// ---------------------------------------------------------------------------

interface EditablePctSubProps {
  pct: number;       // 0-1
  onChange: (newPct: number) => void; // reçoit la nouvelle valeur 0-1
  suffix: string;    // ex " du CA"
  detail?: string;   // ex " — assurances"
}

// Formatte un % en affichage compact : 1 décimale si rond, sinon 2 décimales (ex 12 % / 12,5 % / 12,53 %)
const formatPctSmart = (pct: number): string => {
  const p = pct * 100;
  // Si la 2e décimale est significative, l'afficher
  const rounded2 = Math.round(p * 100) / 100;
  const rounded1 = Math.round(p * 10) / 10;
  if (Math.abs(rounded2 - rounded1) > 0.001) return rounded2.toFixed(2);
  return rounded1.toFixed(1);
};

const EditablePctSub: React.FC<EditablePctSubProps> = ({ pct, onChange, suffix, detail }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(formatPctSmart(pct));

  const start = () => { setDraft(formatPctSmart(pct)); setEditing(true); };
  const commit = () => {
    const v = parseFloat(draft.replace(',', '.'));
    if (!isNaN(v) && v >= 0) onChange(v / 100);
    setEditing(false);
  };
  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="number"
          step="0.01"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          autoFocus
          className="w-16 px-1 py-0.5 text-xs border border-violet-300 rounded outline-none focus:ring-1 focus:ring-violet-400 bg-white tabular-nums text-right"
        />
        <span className="text-xs">% {suffix}{detail}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      className="text-left hover:bg-violet-50 hover:text-violet-700 rounded px-1 -mx-1 transition-colors group"
      title="Cliquer pour modifier — précision 2 décimales (ex 12,53 %)"
    >
      <span className="tabular-nums underline decoration-dotted decoration-gray-300 group-hover:decoration-violet-400 underline-offset-2">{formatPctSmart(pct)} %</span>
      {suffix}{detail}
    </button>
  );
};

// ---------------------------------------------------------------------------
// EditableEurSub — sub-text en € cliquable (pour la charge foncière)
// ---------------------------------------------------------------------------

interface EditableEurSubProps {
  value: number;
  onChange: (newValue: number) => void;
  suffix?: string;
}

const EditableEurSub: React.FC<EditableEurSubProps> = ({ value, onChange, suffix }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(Math.round(value)));

  const start = () => { setDraft(String(Math.round(value))); setEditing(true); };
  const commit = () => {
    const v = parseFloat(draft.replace(/\s/g, '').replace(',', '.'));
    if (!isNaN(v) && v >= 0) onChange(v);
    setEditing(false);
  };
  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="number"
          step="1000"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          autoFocus
          className="w-28 px-1 py-0.5 text-xs border border-violet-300 rounded outline-none focus:ring-1 focus:ring-violet-400 bg-white tabular-nums text-right"
        />
        <span className="text-xs">€ {suffix}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      className="text-left hover:bg-violet-50 hover:text-violet-700 rounded px-1 -mx-1 transition-colors group"
      title="Cliquer pour fixer un montant cible (la marge se recalculera)"
    >
      <span className="underline decoration-dotted decoration-gray-300 group-hover:decoration-violet-400 underline-offset-2">{suffix}</span>
    </button>
  );
};

// ---------------------------------------------------------------------------
// AleasSub — sub-text Aléas : au click, active le paramètre Phase dans le bandeau
// (le % d'aléas n'est pas éditable directement, il est piloté par la phase choisie)
// ---------------------------------------------------------------------------

interface AleasSubProps {
  pct: number;
  isPhaseActive: boolean;
  phaseLabel: PhaseProjet;
  onActivatePhase: () => void;
}

const AleasSub: React.FC<AleasSubProps> = ({ pct, isPhaseActive, phaseLabel, onActivatePhase }) => {
  return (
    <button
      type="button"
      onClick={onActivatePhase}
      className="text-left hover:bg-violet-50 hover:text-violet-700 rounded px-1 -mx-1 transition-colors group inline-flex items-center gap-1"
      title={isPhaseActive ? `Cliquer pour modifier la phase (actuellement ${phaseLabel})` : "Cliquer pour activer le paramètre Phase et choisir le niveau d'avancement"}
    >
      <span className="tabular-nums underline decoration-dotted decoration-gray-300 group-hover:decoration-violet-400 underline-offset-2">{(pct * 100).toFixed(1)} %</span>
      <span>du coût travaux</span>
      {isPhaseActive ? (
        <span className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-700 rounded-full font-medium">phase {phaseLabel}</span>
      ) : (
        <span className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-500 rounded-full font-medium">+ ajouter une phase</span>
      )}
    </button>
  );
};

interface WaterfallRowProps {
  label: string;
  value: number;
  type: 'positif' | 'negatif' | 'total' | 'marge';
  sub?: React.ReactNode;
}
const WaterfallRow: React.FC<WaterfallRowProps> = ({ label, value, type, sub }) => {
  const isNegatif = type === 'negatif';
  const isMarge = type === 'marge';
  const colorClass =
    isMarge ? 'text-green-700' :
    isNegatif ? 'text-gray-700' :
    'text-blue-700';
  const sign = value >= 0 ? '+' : '';
  return (
    <div className={`px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors ${isMarge ? 'bg-green-50' : ''}`}>
      <div>
        <div className={`text-sm font-medium ${isMarge ? 'text-green-900' : 'text-gray-900'}`}>{label}</div>
        {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
      </div>
      <div className={`text-base font-semibold ${colorClass}`}>
        {sign}{formatEuro(value)}
      </div>
    </div>
  );
};

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  displayHint?: string;
  color: 'violet' | 'blue' | 'orange';
  onChange: (n: number) => void;
}
const SliderRow: React.FC<SliderRowProps> = ({ label, value, min, max, step, displayValue, displayHint, color, onChange }) => {
  const accentMap = {
    violet: 'accent-violet-600',
    blue:   'accent-blue-600',
    orange: 'accent-orange-600',
  };
  const textMap = {
    violet: 'text-violet-700',
    blue:   'text-blue-700',
    orange: 'text-orange-700',
  };
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <div className="flex items-baseline gap-3">
          {displayHint && <span className="text-xs text-gray-500">{displayHint}</span>}
          <span className={`text-lg font-bold ${textMap[color]}`}>{displayValue}</span>
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`w-full ${accentMap[color]}`}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// InputRow — saisie directe d'un montant (alternative au SliderRow)
// Utilisé pour les valeurs sans borne naturelle (ex: provision)
// ---------------------------------------------------------------------------

interface InputRowProps {
  label: string;
  value: number;
  unit: string;
  hint?: string;          // texte explicatif sous l'input
  displayHint?: string;   // résumé à droite du label (ex: "150 k€ · 1.0% du CA")
  color: 'violet' | 'blue' | 'orange' | 'amber' | 'gray';
  onChange: (n: number) => void;
}

const InputRow: React.FC<InputRowProps> = ({ label, value, unit, hint, displayHint, color, onChange }) => {
  const focusMap = {
    violet: 'focus:ring-violet-100 focus:border-violet-400',
    blue:   'focus:ring-blue-100 focus:border-blue-400',
    orange: 'focus:ring-orange-100 focus:border-orange-400',
    amber:  'focus:ring-amber-100 focus:border-amber-400',
    gray:   'focus:ring-gray-100 focus:border-gray-400',
  };
  const textMap = {
    violet: 'text-violet-700',
    blue:   'text-blue-700',
    orange: 'text-orange-700',
    amber:  'text-amber-700',
    gray:   'text-gray-700',
  };
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {displayHint && <span className="text-xs text-gray-500">{displayHint}</span>}
      </div>
      <div className="relative max-w-xs">
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          placeholder="0"
          min={0}
          className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-lg font-bold tabular-nums focus:outline-none focus:ring-4 transition-all ${focusMap[color]} ${textMap[color]} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">{unit}</span>
      </div>
      {hint && <p className="text-xs text-gray-500 mt-1.5">{hint}</p>}
    </div>
  );
};

interface ScenarioCardProps {
  label: string;
  programme: Programme;
  bilan: BilanCompute;
  chargeFonciereImplicite: number;
  editable: boolean;
  onChange?: (p: Programme) => void;
  accent: 'violet' | 'blue';
}
const ScenarioCard: React.FC<ScenarioCardProps> = ({ label, programme, bilan, chargeFonciereImplicite, editable, onChange, accent }) => {
  const accentMap = {
    violet: 'border-violet-300 bg-violet-50',
    blue:   'border-blue-300 bg-blue-50',
  };
  const titleMap = {
    violet: 'text-violet-700',
    blue:   'text-blue-700',
  };

  const set = <K extends keyof Programme>(key: K, value: Programme[K]) => {
    if (onChange) onChange({ ...programme, [key]: value });
  };

  return (
    <div className={`rounded-xl border-2 ${accentMap[accent]} p-5`}>
      <div className={`text-sm font-bold ${titleMap[accent]} mb-3`}>{label}</div>

      {editable ? (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <FormField label="Gamme">
            <select
              value={programme.gamme}
              onChange={(e) => set('gamme', e.target.value as Gamme)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
            >
              {(Object.keys(COEF_GAMME) as Gamme[]).map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </FormField>
          <FormField label="PV cible (€/m²)">
            <input
              type="number"
              value={programme.pvCible}
              onChange={(e) => set('pvCible', Number(e.target.value) || 0)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
            />
          </FormField>
          <FormField label="SDP (m²)">
            <input
              type="number"
              value={programme.sdp}
              onChange={(e) => set('sdp', Number(e.target.value) || 0)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
            />
          </FormField>
          <FormField label="RE2025">
            <select
              value={programme.niveauRE2025}
              onChange={(e) => set('niveauRE2025', e.target.value as NiveauRE2025)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
            >
              <option value="N1">N1</option>
              <option value="N2">N2</option>
              <option value="N3">N3</option>
            </select>
          </FormField>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-4">
          <span className="text-xs px-2 py-0.5 bg-white rounded-full text-gray-700">{programme.gamme}</span>
          <span className="text-xs px-2 py-0.5 bg-white rounded-full text-gray-700">{formatInt(programme.pvCible)} €/m²</span>
          <span className="text-xs px-2 py-0.5 bg-white rounded-full text-gray-700">{formatInt(programme.sdp)} m²</span>
          <span className="text-xs px-2 py-0.5 bg-white rounded-full text-gray-700">RE2025 {programme.niveauRE2025}</span>
        </div>
      )}

      <div className="space-y-2 bg-white rounded-lg p-4 border border-gray-200">
        <ScenarioLine label="CA HT"             value={formatEuroM(bilan.caHt)} />
        <ScenarioLine label="Coût travaux"      value={formatEuroM(bilan.travaux)} />
        <ScenarioLine label="Honoraires + frais" value={formatEuroM(bilan.honoraires + bilan.fraisAnnexes + bilan.fraisFinanciers + bilan.fraisCommerciaux + bilan.aleas)} />
        <div className="border-t border-gray-200 pt-2">
          <ScenarioLine label="Charge foncière (marge 8 %)" value={formatEuroM(chargeFonciereImplicite)} strong />
          <ScenarioLine label="→ par logement"  value={`${formatInt(chargeFonciereImplicite / programme.nbLogements)} €`} />
        </div>
      </div>
    </div>
  );
};

interface ScenarioLineProps {
  label: string;
  value: string;
  strong?: boolean;
}
const ScenarioLine: React.FC<ScenarioLineProps> = ({ label, value, strong }) => (
  <div className="flex justify-between text-sm">
    <span className={strong ? 'font-semibold text-gray-900' : 'text-gray-600'}>{label}</span>
    <span className={strong ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}>{value}</span>
  </div>
);

interface DeltaCardProps {
  label: string;
  a: number;
  b: number;
  invertColor?: boolean; // pour les coûts : une hausse est négative
}
const DeltaCard: React.FC<DeltaCardProps> = ({ label, a, b, invertColor }) => {
  const delta = b - a;
  const pct = a !== 0 ? delta / a : 0;
  const positifDelta = delta >= 0;
  const isGood = invertColor ? !positifDelta : positifDelta;
  const colorClass = delta === 0 ? 'text-gray-600' : isGood ? 'text-green-700' : 'text-orange-700';
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${colorClass}`}>
        {positifDelta ? '+' : ''}{formatEuroM(delta)}
      </div>
      <div className={`text-xs font-medium ${colorClass}`}>
        {positifDelta ? '+' : ''}{formatPct(pct)}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CalibrageTab — paramétrage métier expert
// Permet à un expert Sillant d'éditer en dur les coefficients d'estimation,
// indépendamment de la disponibilité statistique. Failsafe au manque de data.
// ---------------------------------------------------------------------------

type ParamSource = 'calcule' | 'force';

interface CalibrageParam {
  key: string;
  label: string;
  coef: number;          // coefficient multiplicateur (1.00 = neutre)
  source: ParamSource;
  sourceLabel: string;   // ex "23 projets" ou "Hypothèse métier"
  updatedBy: string;
  updatedAt: string;     // ISO date string
  note?: string;
}

interface CalibrageSection {
  id: string;
  title: string;
  description: string;
  params: CalibrageParam[];
}

const INITIAL_SECTIONS: CalibrageSection[] = [
  {
    id: 're',
    title: 'Surprime réglementaire',
    description: 'Coefficient appliqué au coût travaux selon la réglementation thermique en vigueur.',
    params: [
      { key: 'RE2020', label: 'RE2020 (sortie réglementaire)', coef: 1.00, source: 'calcule', sourceLabel: 'Calé sur 23 projets', updatedBy: 'Système',         updatedAt: '2026-01-15' },
      { key: 'RE2025', label: 'RE2025 (palier en vigueur)',    coef: 1.08, source: 'force',   sourceLabel: 'Hypothèse métier',  updatedBy: 'Marie Dupont',     updatedAt: '2026-04-22', note: 'Validé comité technique du 18 avril 2026' },
      { key: 'RE2028', label: 'RE2028 (palier à venir)',       coef: 1.12, source: 'force',   sourceLabel: 'Hypothèse prospective', updatedBy: 'Marie Dupont', updatedAt: '2026-05-10', note: 'Aucun projet livré sous RE2028, basé sur retours fournisseurs façades/CVC' },
    ],
  },
  {
    id: 'gamme',
    title: 'Coefficient Gamme',
    description: 'Multiplicateur sur le ratio €/m² médian de la typologie selon le niveau de prestation visé.',
    params: [
      { key: 'eco',  label: 'Économique',         coef: 0.85, source: 'calcule', sourceLabel: 'Calé sur 8 projets',  updatedBy: 'Système',     updatedAt: '2026-02-03' },
      { key: 'int',  label: 'Intermédiaire',      coef: 1.00, source: 'calcule', sourceLabel: 'Calé sur 14 projets', updatedBy: 'Système',     updatedAt: '2026-02-03' },
      { key: 'hdg',  label: 'Haut de gamme',      coef: 1.30, source: 'force',   sourceLabel: 'Hypothèse métier',    updatedBy: 'Pierre Roux', updatedAt: '2026-03-12', note: 'Échantillon trop faible (2 projets) — coef forcé à partir de benchmarks FPI' },
      { key: 'thdg', label: 'Très haut de gamme', coef: 1.55, source: 'force',   sourceLabel: 'Hypothèse métier',    updatedBy: 'Pierre Roux', updatedAt: '2026-03-12' },
    ],
  },
  {
    id: 'devolution',
    title: 'Coefficient Dévolution',
    description: 'Impact du mode de dévolution du marché sur le coût travaux global.',
    params: [
      { key: 'eg',   label: 'Entreprise Générale',     coef: 1.00, source: 'calcule', sourceLabel: 'Calé sur 11 projets', updatedBy: 'Système',     updatedAt: '2026-01-15' },
      { key: 'ml',   label: 'Macro-lot',               coef: 0.95, source: 'calcule', sourceLabel: 'Calé sur 6 projets',  updatedBy: 'Système',     updatedAt: '2026-01-15' },
      { key: 'ces',  label: 'Corps d\'État Séparés',   coef: 0.92, source: 'force',   sourceLabel: 'Hypothèse métier',    updatedBy: 'Marie Dupont', updatedAt: '2026-02-28', note: 'Benchmark littérature MOP, échantillon Sillant insuffisant (2 projets)' },
      { key: 'cg',   label: 'Contractant Général',     coef: 1.05, source: 'force',   sourceLabel: 'Hypothèse métier',    updatedBy: 'Marie Dupont', updatedAt: '2026-02-28' },
    ],
  },
];

const CalibrageTab: React.FC = () => {
  const [sections, setSections] = useState<CalibrageSection[]>(INITIAL_SECTIONS);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['re']));

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateCoef = (sectionId: string, paramKey: string, newCoef: number) => {
    setSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      return {
        ...s,
        params: s.params.map(p => p.key === paramKey ? {
          ...p,
          coef: newCoef,
          source: 'force' as ParamSource,
          sourceLabel: 'Modifié manuellement',
          updatedBy: 'Vous',
          updatedAt: new Date().toISOString().slice(0, 10),
        } : p),
      };
    }));
  };

  const resetAll = () => setSections(INITIAL_SECTIONS);

  const totalForce = sections.reduce(
    (sum, s) => sum + s.params.filter(p => p.source === 'force').length,
    0,
  );
  const totalCalcule = sections.reduce(
    (sum, s) => sum + s.params.filter(p => p.source === 'calcule').length,
    0,
  );

  return (
    <>
      {/* Notice d'intro */}
      <section className="mb-6 bg-gradient-to-br from-violet-50/60 to-white border border-violet-100 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <BeakerIcon className="w-5 h-5 text-violet-700" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Calibrage métier des coefficients d'estimation</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Ces coefficients pondèrent les ratios €/m² du marché selon les paramètres de l'opération.
              Quand la data Sillant est suffisante (≥ 5 projets comparables), ils sont <span className="font-medium text-green-700">calés automatiquement</span>.
              Sinon, ils peuvent être <span className="font-medium text-orange-700">forcés manuellement</span> à partir d'hypothèses métier expertes — c'est un failsafe au manque de data.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                {totalCalcule} calés automatiquement
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-700">
                <span className="w-2 h-2 rounded-full bg-orange-500" />
                {totalForce} forcés manuellement
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-violet-400 hover:text-violet-700 transition-colors"
            >
              <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
              Réinitialiser
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-violet-400 hover:text-violet-700 transition-colors"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              Exporter
            </button>
          </div>
        </div>
      </section>

      {/* Sections de paramètres */}
      <div className="space-y-4">
        {sections.map(section => {
          const isOpen = expandedSections.has(section.id);
          const nbForce = section.params.filter(p => p.source === 'force').length;
          return (
            <section key={section.id} className="bg-white shadow rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheckIcon className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">{section.title}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-500">
                    {section.params.length} valeur{section.params.length > 1 ? 's' : ''}
                    {nbForce > 0 && <span className="text-orange-600 font-medium"> · {nbForce} forcé{nbForce > 1 ? 's' : ''}</span>}
                  </span>
                  {isOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-gray-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="text-left font-medium px-6 py-2">Paramètre</th>
                        <th className="text-right font-medium px-3 py-2">Coefficient</th>
                        <th className="text-right font-medium px-3 py-2">Effet</th>
                        <th className="text-left font-medium px-3 py-2">Source</th>
                        <th className="text-left font-medium px-6 py-2">Dernière modification</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {section.params.map(p => (
                        <CalibrageRow
                          key={p.key}
                          param={p}
                          onCoefChange={(v) => updateCoef(section.id, p.key, v)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Footer note */}
      <section className="mt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Comment ces coefficients sont utilisés</p>
            <p className="text-blue-700 leading-relaxed">
              Chaque estimation multiplie le ratio médian du marché (par typologie) par les coefficients applicables :
              <span className="font-mono text-xs ml-1 px-1.5 py-0.5 bg-white rounded">ratio × Gamme × Dévolution × RE × Géo × Phase</span>.
              Toute modification ici est <strong>tracée</strong> (auteur, date, note) et sera <strong>versionnée par opération</strong> dans la prochaine itération
              (chaque étude se figera sur les coefficients à la date de validation comité).
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// CalibrageRow — une ligne de coefficient éditable
// ---------------------------------------------------------------------------

interface CalibrageRowProps {
  param: CalibrageParam;
  onCoefChange: (v: number) => void;
}

const CalibrageRow: React.FC<CalibrageRowProps> = ({ param, onCoefChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(param.coef));

  const isForce = param.source === 'force';
  const effetPct = (param.coef - 1);

  const start = () => { setDraft(String(param.coef)); setEditing(true); };
  const commit = () => {
    const v = parseFloat(draft.replace(',', '.'));
    if (!isNaN(v) && v > 0) onCoefChange(v);
    setEditing(false);
  };
  const cancel = () => { setDraft(String(param.coef)); setEditing(false); };

  return (
    <tr className="hover:bg-gray-50/50 transition-colors">
      <td className="px-6 py-3">
        <div className="font-medium text-gray-900">{param.label}</div>
        {param.note && (
          <div className="text-xs text-gray-500 mt-0.5 italic">{param.note}</div>
        )}
      </td>
      <td className="px-3 py-3 text-right">
        {editing ? (
          <input
            type="number"
            step="0.01"
            min="0"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
              if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            autoFocus
            className="w-20 px-2 py-1 text-right text-sm font-semibold tabular-nums border border-violet-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-200"
          />
        ) : (
          <button
            type="button"
            onClick={start}
            className="px-2 py-1 text-sm font-semibold tabular-nums text-gray-900 hover:bg-violet-50 hover:text-violet-700 rounded transition-colors"
            title="Cliquer pour modifier"
          >
            {param.coef.toFixed(2)}
          </button>
        )}
      </td>
      <td className="px-3 py-3 text-right">
        <span className={`text-sm font-medium tabular-nums ${
          effetPct === 0 ? 'text-gray-400' :
          effetPct > 0   ? 'text-orange-600' :
                           'text-green-600'
        }`}>
          {effetPct === 0 ? '—' : `${effetPct > 0 ? '+' : ''}${(effetPct * 100).toFixed(1)} %`}
        </span>
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${
          isForce ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isForce ? 'bg-orange-500' : 'bg-green-500'}`} />
          {param.sourceLabel}
        </span>
      </td>
      <td className="px-6 py-3 text-xs text-gray-500">
        <div>{param.updatedBy}</div>
        <div className="text-gray-400">{new Date(param.updatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// ModulesPanel — bouton "+ Modules" dans la nav d'onglets + panneau de gestion
// Pattern Sillant : bouton dashed gris qui révèle un dropdown avec checkboxes.
// L'opération porte la liste des modules actifs → partagée entre collaborateurs.
// ---------------------------------------------------------------------------

interface ModulesPanelProps {
  modulesActifs: Set<ModuleId>;
  onToggle: (id: ModuleId) => void;
}

const ModulesPanel: React.FC<ModulesPanelProps> = ({ modulesActifs, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // On ne montre QUE les modules optionnels (activables). Les requis sont implicites.
  const modulesOptionnels = MODULES_LIST.filter(m => !m.required);
  const nbDisponibles = modulesOptionnels.filter(m => !modulesActifs.has(m.id)).length;
  const nbActifs = modulesOptionnels.filter(m => modulesActifs.has(m.id)).length;

  // Regroupement par catégorie (sans les requis)
  const modulesByCategory: Record<ModuleCategory, ModuleDef[]> = {
    core:    modulesOptionnels.filter(m => m.category === 'core'),
    analyse: modulesOptionnels.filter(m => m.category === 'analyse'),
    admin:   modulesOptionnels.filter(m => m.category === 'admin'),
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className={`group inline-flex items-center gap-1.5 py-2 px-3 rounded-lg border border-dashed text-sm font-medium transition-colors ${
          isOpen
            ? 'border-violet-400 bg-violet-50 text-violet-700'
            : 'border-gray-300 text-gray-500 hover:border-violet-400 hover:text-violet-700 hover:bg-violet-50/50'
        }`}
        title="Activer ou désactiver des modules de cette opération"
      >
        <PlusIcon className="w-3.5 h-3.5" />
        <span>Modules</span>
        {nbDisponibles > 0 && (
          <span className={`ml-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5 transition-colors ${
            isOpen
              ? 'bg-violet-100 text-violet-700'
              : 'bg-gray-100 text-gray-500 group-hover:bg-violet-100 group-hover:text-violet-700'
          }`}>
            +{nbDisponibles}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl w-[420px] overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Modules optionnels</h3>
              <span className="text-xs text-gray-500 tabular-nums">{nbActifs} / {modulesOptionnels.length} actifs</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Activez les capacités dont vous avez besoin. <span className="font-medium text-gray-700">Les modules sont partagés</span> avec vos collaborateurs sur cette étude.
            </p>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {(['core', 'analyse', 'admin'] as ModuleCategory[]).map(cat => {
              const mods = modulesByCategory[cat];
              if (mods.length === 0) return null;
              return (
                <div key={cat} className="py-1.5">
                  <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    {CATEGORY_LABELS[cat]}
                  </div>
                  {mods.map(m => {
                    const isActive = modulesActifs.has(m.id);
                    const Icon = MODULE_ICONS[m.id];
                    return (
                      <label
                        key={m.id}
                        className="flex items-start gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={() => onToggle(m.id)}
                          className="mt-1 w-4 h-4 accent-violet-600 flex-shrink-0"
                        />
                        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 transition-colors ${isActive ? 'text-violet-600' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>{m.label}</span>
                          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{m.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/60 text-[11px] text-gray-500 leading-relaxed">
            💡 Les modules désactivés <strong>conservent leurs données</strong> — réactivez à tout moment.
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// NewStudyModal — modale de choix de template au démarrage d'une nouvelle étude
// 4 templates : Opportunité foncier · Faisabilité esquisse · Comité · Vierge
// Chaque template applique un preset de modules + paramètres optionnels + programme
// ---------------------------------------------------------------------------

interface NewStudyModalProps {
  onClose: () => void;
  onChoose: (template: TemplateDef) => void;
}

const NewStudyModal: React.FC<NewStudyModalProps> = ({ onClose, onChoose }) => {
  // Fermeture sur Échap
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-study-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h2 id="new-study-title" className="text-xl font-semibold text-gray-900 tracking-tight">
              Nouvelle étude
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Choisissez un point de départ. Vous pourrez activer ou désactiver des modules à tout moment.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Templates grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TEMPLATES_LIST.map(t => (
              <TemplateCard key={t.id} template={t} onChoose={() => onChoose(t)} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50/60 border-t border-gray-100 text-[11px] text-gray-500 leading-relaxed">
          💡 Les templates pré-activent les modules les plus pertinents. Vous pourrez en ajouter ou en retirer via le bouton <strong>+ Modules</strong>.
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TemplateCard — une carte cliquable représentant un template
// ---------------------------------------------------------------------------

interface TemplateCardProps {
  template: TemplateDef;
  onChoose: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onChoose }) => {
  const Icon = TEMPLATE_ICONS[template.id];

  const accentMap: Record<TemplateDef['accent'], { bg: string; border: string; iconBg: string; iconText: string; hover: string }> = {
    violet: { bg: 'bg-violet-50/30', border: 'border-violet-200', iconBg: 'bg-violet-100', iconText: 'text-violet-700', hover: 'hover:border-violet-400 hover:shadow-violet-100' },
    blue:   { bg: 'bg-blue-50/30',   border: 'border-blue-200',   iconBg: 'bg-blue-100',   iconText: 'text-blue-700',   hover: 'hover:border-blue-400 hover:shadow-blue-100' },
    green:  { bg: 'bg-green-50/30',  border: 'border-green-200',  iconBg: 'bg-green-100',  iconText: 'text-green-700',  hover: 'hover:border-green-400 hover:shadow-green-100' },
    gray:   { bg: 'bg-gray-50/30',   border: 'border-gray-200',   iconBg: 'bg-gray-100',   iconText: 'text-gray-700',   hover: 'hover:border-gray-400 hover:shadow-gray-100' },
  };
  const c = accentMap[template.accent];

  return (
    <button
      type="button"
      onClick={onChoose}
      className={`group text-left rounded-xl border p-5 transition-all duration-150 hover:shadow-lg ${c.bg} ${c.border} ${c.hover}`}
    >
      {/* Header : icône + titre + persona */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
          <Icon className={`w-5 h-5 ${c.iconText}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 group-hover:text-gray-900">{template.label}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{template.tagline}</p>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 leading-relaxed mb-4">{template.description}</p>

      {/* Méta */}
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-gray-500">
          Pour : <span className="font-medium text-gray-700">{template.targetPersona}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-gray-400">
          {template.modules.filter(m => m !== 'calibrage').length} module{template.modules.filter(m => m !== 'calibrage').length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Modules pré-activés (badges) */}
      <div className="mt-3 pt-3 border-t border-gray-200/60 flex flex-wrap gap-1">
        {template.modules.filter(m => m !== 'calibrage').map(modId => {
          const def = MODULES_LIST.find(mm => mm.id === modId);
          if (!def) return null;
          return (
            <span key={modId} className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-600">
              {def.label}
            </span>
          );
        })}
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// VarianteSelector — dropdown matériau pour le Mode Précis
// Détecte la variante actuelle (si le ratio matche) et permet d'en changer.
// L'application d'une variante = override 'ratio' avec la valeur de la variante.
// ---------------------------------------------------------------------------

interface VarianteSelectorProps {
  variantes: LotVariante[];
  currentRatio: number;   // ratio actuellement affiché (pour détecter la variante active)
  onSelect: (v: LotVariante) => void;
  onReset: () => void;
}

const VarianteSelector: React.FC<VarianteSelectorProps> = ({ variantes, currentRatio, onSelect, onReset }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Détection de la variante active (ratio matche à 5 € près)
  const activeVariante = variantes.find(v => Math.abs(v.ratio - currentRatio) < 5);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        title="Choisir un matériau (Mode Précis)"
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border transition-colors ${
          activeVariante
            ? 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100'
            : 'border-dashed border-gray-300 text-gray-500 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50'
        }`}
      >
        <SparklesIcon className="w-3 h-3" />
        <span className="max-w-[140px] truncate">{activeVariante ? activeVariante.label : 'Choisir matériau'}</span>
        <ChevronDownIcon className="w-3 h-3 opacity-60" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-xl w-[320px] py-1.5">
          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
            Variantes matériaux
          </div>
          {variantes.map(v => {
            const isActive = activeVariante?.key === v.key;
            return (
              <button
                key={v.key}
                type="button"
                onClick={() => { onSelect(v); setIsOpen(false); }}
                className={`w-full text-left px-3 py-2 hover:bg-violet-50 hover:text-violet-700 transition-colors ${
                  isActive ? 'bg-violet-50/60' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isActive && <CheckIcon className="w-3 h-3 text-violet-600 flex-shrink-0" />}
                    <span className={`text-sm font-medium ${isActive ? 'text-violet-700' : 'text-gray-900'}`}>{v.label}</span>
                  </div>
                  <span className="text-xs tabular-nums font-semibold text-violet-600 flex-shrink-0">{formatInt(v.ratio)} €/m²</span>
                </div>
                {v.description && (
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{v.description}</p>
                )}
              </button>
            );
          })}
          {activeVariante && (
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                type="button"
                onClick={() => { onReset(); setIsOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 inline-flex items-center gap-1.5"
              >
                <ArrowUturnLeftIcon className="w-3 h-3" />
                Revenir au ratio marché
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// EstimationBilanFusion — module fusionné Estimation + Bilan d'opération
// Structure : KPI strip (sticky) → coût travaux dominant → répartition par lot
//                                → accordion "Bilan détaillé" (waterfall + % éditables)
// ---------------------------------------------------------------------------

interface EstimationBilanFusionProps {
  programme: Programme;
  bilan: BilanCompute;
  abaque: AbaqueRatios;
  baseTravaux: number;
  hasOverrides: boolean;
  activeOptional: Set<OptionalParamKey>;
  lotOverrides: Map<string, LotOverride>;
  onUpdateOverride: (categorie: string, partial: Partial<LotOverride>) => void;
  onResetOverride: (categorie: string) => void;
  onResetAll: () => void;
  onActivatePhase: () => void;
  // State remonté pour synchroniser KPI strip ↔ waterfall détaillé
  localPcts: LocalPcts;
  setPct: (key: keyof LocalPcts, v: number) => void;
  chargeFonciereOverride: number | null;
  setChargeFonciereOverride: (v: number | null) => void;
  // Valeurs effectives calculées au parent
  honorairesEff: number;
  fraisAnnexesEff: number;
  fraisFinanciersEff: number;
  fraisCommerciauxEff: number;
  totalHorsTerrainEff: number;
  margeCibleEff: number;
  margeCiblePctEff: number;
  chargeFonciereImpliciteEff: number;
}

const EstimationBilanFusion: React.FC<EstimationBilanFusionProps> = (props) => {
  // Accordion Bilan détaillé — replié par défaut (le MOA voit son coût et son KPI strip ; il déplie si besoin)
  const [bilanOpen, setBilanOpen] = useState(false);

  // Les valeurs affichées proviennent des props (calculées au parent), garantissant
  // que toute édition dans le waterfall détaillé impacte instantanément le KPI strip.
  const isFraisAcq = props.activeOptional.has('fraisAcquisitionPct');
  const { prixAchat } = decomposerCharge(props.chargeFonciereImpliciteEff, props.programme, props.activeOptional);
  const isTVA = props.activeOptional.has('regimeTVA');

  return (
    <>
      {/* ===== KPI Strip — bandeau sticky avec les 5 chiffres clés du bilan ===== */}
      <section className="mb-4 sticky top-[88px] z-10">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3">
          <div className="flex items-center gap-6 overflow-x-auto">
            <BilanKpiCompact
              label={isTVA ? 'CA HT' : 'CA total'}
              value={formatEuroM(props.bilan.caHt)}
              color="blue"
              sub={isTVA ? `TTC ${formatEuroM(props.bilan.caTtc)}` : undefined}
            />
            <BilanKpiDivider />
            <BilanKpiCompact
              label="Coût travaux"
              value={formatEuroM(props.bilan.travaux)}
              color="violet"
              sub={`${formatInt(props.bilan.travaux / Math.max(props.programme.sdp, 1))} €/m²`}
            />
            <BilanKpiDivider />
            <BilanKpiCompact
              label={isFraisAcq ? "Prix d'achat terrain" : 'Charge foncière implicite'}
              value={formatEuroM(isFraisAcq ? prixAchat : props.chargeFonciereImpliciteEff)}
              color="orange"
              sub={`${formatInt((isFraisAcq ? prixAchat : props.chargeFonciereImpliciteEff) / Math.max(props.programme.nbLogements, 1))} €/logt`}
            />
            <BilanKpiDivider />
            <BilanKpiCompact
              label="Marge prévisionnelle"
              value={formatEuroM(props.margeCibleEff)}
              color="green"
              sub={`${formatPct(props.margeCiblePctEff)} ${props.chargeFonciereOverride !== null ? 'recalculée' : 'cible'}`}
            />
            <BilanKpiDivider />
            <BilanKpiCompact
              label="Prix de revient"
              value={formatEuroM(props.totalHorsTerrainEff + props.chargeFonciereImpliciteEff)}
              color="gray"
            />
          </div>
        </div>
      </section>

      {/* ===== Section Estimation travaux ===== */}
      <EstimationTab
        programme={props.programme}
        bilan={props.bilan}
        baseTravaux={props.baseTravaux}
        activeOptional={props.activeOptional}
        lotOverrides={props.lotOverrides}
        onUpdateOverride={props.onUpdateOverride}
        onResetOverride={props.onResetOverride}
        onResetAll={props.onResetAll}
      />

      {/* ===== Accordion Bilan détaillé ===== */}
      <section className="mt-6 bg-white shadow rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setBilanOpen(o => !o)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <ScaleIcon className="w-5 h-5 text-violet-600 flex-shrink-0" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">Bilan d'opération détaillé</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Waterfall complet, % éditables (honoraires, frais, marge), décomposition prix terrain — modifiez en cascade.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {props.hasOverrides && (
              <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-medium">
                Personnalisé
              </span>
            )}
            <span className="text-xs text-gray-500">{bilanOpen ? 'Replier' : 'Déplier'}</span>
            {bilanOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
          </div>
        </button>

        {bilanOpen && (
          <div className="border-t border-gray-100 px-6 py-5 bg-gray-50/30">
            <BilanTab
              programme={props.programme}
              bilan={props.bilan}
              abaque={props.abaque}
              hasOverrides={props.hasOverrides}
              activeOptional={props.activeOptional}
              onActivatePhase={props.onActivatePhase}
              localPcts={props.localPcts}
              setPct={props.setPct}
              chargeFonciereOverride={props.chargeFonciereOverride}
              setChargeFonciereOverride={props.setChargeFonciereOverride}
              honorairesEff={props.honorairesEff}
              fraisAnnexesEff={props.fraisAnnexesEff}
              fraisFinanciersEff={props.fraisFinanciersEff}
              fraisCommerciauxEff={props.fraisCommerciauxEff}
              totalHorsTerrainEff={props.totalHorsTerrainEff}
              margeCibleEff={props.margeCibleEff}
              margeCiblePctEff={props.margeCiblePctEff}
              chargeFonciereImpliciteEff={props.chargeFonciereImpliciteEff}
            />
          </div>
        )}
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// BilanKpiCompact — un KPI compact pour le strip en haut de l'Estimation+Bilan
// ---------------------------------------------------------------------------

interface BilanKpiCompactProps {
  label: string;
  value: string;
  sub?: string;
  color: 'blue' | 'violet' | 'orange' | 'green' | 'gray';
}

const BilanKpiCompact: React.FC<BilanKpiCompactProps> = ({ label, value, sub, color }) => {
  const colorMap = {
    blue:   'text-blue-700',
    violet: 'text-violet-700',
    orange: 'text-orange-700',
    green:  'text-green-700',
    gray:   'text-gray-700',
  };
  return (
    <div className="flex-shrink-0 min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500 truncate">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${colorMap[color]} truncate`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400 truncate">{sub}</div>}
    </div>
  );
};

const BilanKpiDivider: React.FC = () => (
  <div className="w-px h-10 bg-gray-200 flex-shrink-0" aria-hidden="true" />
);

export default EtudeOpportunite;
